import { pgTable, pgEnum, text, varchar, timestamp, boolean, uuid, date, integer, numeric, jsonb, bigint, smallint, index, unique, uniqueIndex, check, doublePrecision } from "drizzle-orm/pg-core";
import { sql, relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import type { AuthUserId, ChatReadMarker, ProfileId } from "./identity";
import {
  ROBOT_CLIENT_STATUSES,
  ROBOT_FLEET_ASSIGNABLE_STATUSES,
  ROBOT_FLEET_STATUS_VALUES,
} from "./robotStatusConfig";

// --- Helper de coercition date réutilisable ---
// Chaîne vide = null (jamais ""), toute autre valeur (string ISO, Date, null) est
// coercée vers un objet Date. Clé absente du body = undefined (champ non touché,
// important pour les mises à jour partielles).
const emptyStringToNull = (v: unknown) => (v === "" ? null : v);
export const zDateNullable = z.preprocess(emptyStringToNull, z.coerce.date().nullable().optional());

// --- DB Enums (PostgreSQL) ---
export const revendeurTypeEnum = pgEnum("revendeur_type", ["multi_sites", "centrale_achat", "independant"]);
export const siteTypeEnum = pgEnum("site_type", ["point_de_vente", "depot", "sav", "siege", "autre"]);
export const geocodeStatutPgEnum = pgEnum("geocode_statut", ["a_faire", "ok", "echec"]);

// --- Enums ---
export const roleEnum = z.enum(["admin", "planification", "technicien", "commercial", "administration"]);
export const commercialStatusEnum = z.enum(["salarie", "agent_commercial", "alternant"]);
export const dayStatusEnum = z.enum(["bureau", "remote", "absent", "conges", "alternance"]);
export const categoryEnum = z.enum(["livraison", "nettoyage", "industriel"]);
export const robotTypeEnum = z.enum(["demo", "vendu", "client"]);
export const robotStatusEnum = z.enum(
  ROBOT_FLEET_ASSIGNABLE_STATUSES.map((status) => status.value) as [
    "disponible" | "en_mission" | "en_panne" | "en_reparation",
    ...("disponible" | "en_mission" | "en_panne" | "en_reparation")[],
  ],
);
export const locationTypeEnum = z.enum(["siege", "mission", "remplacement", "autre"]);
export const clientTypeEnum = z.enum(["prospect", "client"]);
export const prospectStatusEnum = z.enum(["stand_by", "date_proposee", "demo_planifiee", "demo_realisee", "gagne", "perdu"]);
export const contractTypeEnum = z.enum(["achat", "location", "poc"]);
export const vehicleStatusEnum = z.enum(["disponible", "en_mission"]);
export const missionTypeEnum = z.enum(["demonstration", "cdc", "installation", "intervention_sav", "maintenance", "formation", "atelier", "location", "livraison", "trajet", "salon", "salon_interne", "salon_partenaire", "prestation_evenement_client", "bureau", "organisation"]);
export const missionPhaseEnum = z.enum(["montage", "presence", "demontage"]);
export const taskTypeEnum = z.enum(["reservation_hotel", "reservation_train", "location_vehicule", "preparation_materiel", "autre"]);
export const taskStatusEnum = z.enum(["a_faire", "en_cours", "fait"]);
export const missionStatusEnum = z.enum(["option", "confirme", "en_cours", "termine", "annule"]);
export const resellerStatusEnum = z.enum(["revendeur", "revendeur_premium", "sous_distributeur", "apporteur_affaires", "annule"]);
export const ticketSourceEnum = z.enum(["formulaire", "email", "telephone", "chat"]);
export const ticketPriorityEnum = z.enum(["faible", "moyen", "eleve", "urgent"]);
export const ticketStatusEnum = z.enum(["nouveau", "en_cours", "en_attente_piece", "resolu", "ferme"]);

// --- Tables ---

export const agences = pgTable("agences", {
  id: uuid("id").primaryKey().defaultRandom(),
  code: text("code").notNull().unique(),
  libelle: text("libelle").notNull(),
  ville: text("ville"),
  googleCalendarId: text("google_calendar_id"),
  actif: boolean("actif").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});

// 4.1 profiles (extended auth.users)
export const profiles = pgTable("profiles", {
  id: uuid("id").$type<ProfileId>().primaryKey(),
  role: text("role", { enum: ["admin", "planification", "technicien", "commercial", "administration"] }).notNull(),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  email: text("email"),
  phone: text("phone"),
  avatarUrl: text("avatar_url"),
  commercialStatus: text("commercial_status", { enum: ["salarie", "agent_commercial", "alternant"] }),
  adminRole: text("admin_role"),
  accessRole: text("access_role", { enum: ["technicien", "manager", "admin", "super_admin"] }).notNull().default("technicien"),
  invitedAt: timestamp("invited_at", { withTimezone: true }),
  hasDrivingLicense: boolean("has_driving_license").default(false),
  isActive: boolean("is_active").default(true),
  rolesSecondaires: text("roles_secondaires").array().notNull().default(sql`'{}'`),
  hubspotLicenceEcriture: boolean("hubspot_licence_ecriture").default(false),
  intervenantTerrain: boolean("intervenant_terrain").default(false),
  gestionPlanning: boolean("gestion_planning").default(false),
  agenceId: uuid("agence_id").references(() => agences.id, { onDelete: "restrict" }),
  authUserId: uuid("auth_user_id").$type<AuthUserId>(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 4.2 technician_schedules
export const technicianSchedules = pgTable("technician_schedules", {
  id: uuid("id").primaryKey().defaultRandom(),
  technicianId: uuid("technician_id").$type<ProfileId>().references(() => profiles.id).notNull(),
  weekStart: date("week_start").notNull(),
  mondayStatus: text("monday_status", { enum: ["bureau", "remote", "absent", "conges", "alternance"] }),
  tuesdayStatus: text("tuesday_status", { enum: ["bureau", "remote", "absent", "conges", "alternance"] }),
  wednesdayStatus: text("wednesday_status", { enum: ["bureau", "remote", "absent", "conges", "alternance"] }),
  thursdayStatus: text("thursday_status", { enum: ["bureau", "remote", "absent", "conges", "alternance"] }),
  fridayStatus: text("friday_status", { enum: ["bureau", "remote", "absent", "conges", "alternance"] }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 4.3 robots
export const robots = pgTable("robots", {
  id: uuid("id").primaryKey().defaultRandom(),
  sku: text("sku").unique().notNull(),
  serialNumber: text("serial_number").unique().notNull(),
  macAddress: text("mac_address").unique(),
  modelName: text("model_name").notNull(),
  productModel: text("product_model"),
  nickname: text("nickname"),
  color: text("color"),
  category: text("category", { enum: ["livraison", "nettoyage", "industriel"] }).notNull(),
  robotType: text("robot_type", { enum: ["demo", "vendu", "client"] }).notNull(),
  status: text("status", {
    enum: ROBOT_FLEET_ASSIGNABLE_STATUSES.map((status) => status.value) as [string, ...string[]],
  }).notNull().default(ROBOT_FLEET_STATUS_VALUES.DISPONIBLE),
  imageUrl: text("image_url"),
  catalogSku: text("catalog_sku"),
  profilePhotoUrl: text("profile_photo_url"),
  qrCodeUrl: text("qr_code_url"),
  photoFrontUrl: text("photo_front_url"),
  photoBackUrl: text("photo_back_url"),
  photoLeftUrl: text("photo_left_url"),
  photoRightUrl: text("photo_right_url"),
  notes: text("notes"),
  puduMagasin: text("pudu_magasin"),
  statutAtelier: text("statut_atelier"),
  provenanceActuelle: text("provenance_actuelle"),
  localisationActuelle: text("localisation_actuelle"),
  localisationDetail: text("localisation_detail"),
  statutChangedAt: timestamp("statut_changed_at", { withTimezone: true }),
  statutChangedBy: uuid("statut_changed_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 4.4 clients (enriched)
export const clients = pgTable("clients", {
  id: uuid("id").primaryKey().defaultRandom(),
  clientNumber: text("client_number"),
  numeroClient: integer("numero_client").unique(),
  companyName: text("company_name").notNull(),
  nomCommercial: text("nom_commercial"),
  clientType: text("client_type", { enum: ["prospect", "client"] }).notNull(),
  siren: text("siren"),
  sector: text("sector"),
  groupName: text("group_name"),
  contractType: text("contract_type", { enum: ["achat", "location", "poc"] }),
  leaser: text("leaser"),
  address: text("address").notNull(),
  contactCivility: text("contact_civility", { enum: ["M.", "Mme"] }),
  contactName: text("contact_name").notNull(),
  contactPhone: text("contact_phone").notNull(),
  contactEmail: text("contact_email").notNull(),
  environmentType: text("environment_type"),
  commercialId: uuid("commercial_id").$type<ProfileId>().references(() => profiles.id).notNull(),
  contractStartDate: date("contract_start_date"),
  contractEndDate: date("contract_end_date"),
  installerTechnicianId: uuid("installer_technician_id").$type<ProfileId>().references(() => profiles.id),
  maintenanceContract: boolean("maintenance_contract").default(false),
  maintenanceDateDebut: date("maintenance_date_debut"),
  maintenanceActive: boolean("maintenance_active").default(true),
  logoUrl: text("logo_url"),
  website: text("website"),
  nbSalaries: integer("nb_salaries"),
  couleurPrimaire: text("couleur_primaire"),
  couleurSecondaire: text("couleur_secondaire"),
  notes: text("notes"),
  emailBienvenueEnvoyeAt: timestamp("email_bienvenue_envoye_at", { withTimezone: true }),
  reclassifieDepuisRevendeurId: uuid("reclassifie_depuis_revendeur_id").references(() => resellers.id),
  technicienReferentId: uuid("technicien_referent_id").$type<ProfileId>().references(() => profiles.id),
  siteInstallation: text("site_installation"),
  venteDeclareAt: timestamp("vente_declaree_at", { withTimezone: true }),
  declarationToken: text("declaration_token"),
  declarationTokenExpiresAt: timestamp("declaration_token_expires_at", { withTimezone: true }),
  declarationStatut: text("declaration_statut"),
  declarationEnvoyeeA: text("declaration_envoyee_a"),
  robotsDeclares: text("robots_declares"),
  commentaireDeclaration: text("commentaire_declaration"),
  ficheACompleter: boolean("fiche_a_completer").default(false),
  pays: text("pays"),
  adresseFacturation: jsonb("adresse_facturation"),
  adresseInstallation: jsonb("adresse_installation"),
  secteurCategorie: text("secteur_categorie"),
  robotsDeclaresJson: jsonb("robots_declares_json"),
  accessoiresDeclares: jsonb("accessoires_declares"),
  declarationLienEnvoyeAt: timestamp("declaration_lien_envoye_at", { withTimezone: true }),
  declarationRelancesCount: integer("declaration_relances_count").default(0),
  declarationDerniereRelanceAt: timestamp("declaration_derniere_relance_at", { withTimezone: true }),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  geocodingStatus: text("geocoding_status").default("pending"),
  geocodedAddress: text("geocoded_address"),
  geocodingScore: doublePrecision("geocoding_score"),
  geocodingLabel: text("geocoding_label"),
  departementSiege: text("departement_siege"),
  geocodedAt: timestamp("geocoded_at", { withTimezone: true }),
  puduShopId: text("pudu_shop_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqPuduShopId: uniqueIndex("clients_pudu_shop_id_uniq").on(t.puduShopId),
}));

// 4.4b prospects
export const prospects = pgTable("prospects", {
  id: uuid("id").primaryKey().defaultRandom(),
  societe: text("societe").notNull(),
  secteurActivite: text("secteur_activite"),
  groupe: text("groupe"),
  civiliteContact: text("civilite_contact", { enum: ["M.", "Mme"] }),
  prenomContact: text("prenom_contact"),
  nomContact: text("nom_contact"),
  email: text("email"),
  telephone: text("telephone"),
  adresse: text("adresse"),
  codePostal: text("code_postal"),
  ville: text("ville"),
  pays: text("pays").default("France"),
  commercialId: uuid("commercial_id").$type<ProfileId>().references(() => profiles.id),
  statut: text("statut", { enum: ["stand_by", "date_proposee", "demo_planifiee", "demo_realisee", "gagne", "perdu"] }).notNull().default("stand_by"),
  datePremierContact: date("date_premier_contact"),
  notes: text("notes"),
  transformeEnClientId: uuid("transforme_en_client_id").references(() => clients.id),
  transformeEnRevendeurId: uuid("transforme_en_revendeur_id").references(() => resellers.id),
  actif: boolean("actif").default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 4.5 vehicles
export const vehicles = pgTable("vehicles", {
  id: uuid("id").primaryKey().defaultRandom(),
  licensePlate: text("license_plate").unique().notNull(),
  vehicleType: text("vehicle_type").notNull(),
  brandModel: text("brand_model").notNull(),
  nature: text("nature", { enum: ["parc", "transport"] }).notNull().default("parc"),
  agenceId: uuid("agence_id").references(() => agences.id, { onDelete: "restrict" }),
  status: text("status", { enum: ["disponible", "en_mission"] }).notNull().default("disponible"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 4.6 products
export const products = pgTable("products", {
  id: uuid("id").primaryKey().defaultRandom(),
  imageUrl: text("image_url"),
  robotsCompatibles: text("robots_compatibles").array(),
  nomFr: text("nom_fr").notNull(),
  nomEn: text("nom_en"),
  sku: text("sku").unique(),
  garantie: boolean("garantie").notNull().default(false),
  descriptionFr: text("description_fr"),
  descriptionEn: text("description_en"),
  type: text("type", { enum: ["piece_detachee", "consommable", "machine", "accessoire", "iot", "robot", "service"] }).notNull(),
  prixHt: numeric("prix_ht"),
  prixDistribution: numeric("prix_distribution", { precision: 10, scale: 2 }),
  prixRevendeurPremium: numeric("prix_revendeur_premium", { precision: 10, scale: 2 }),
  prixRevendeur: numeric("prix_revendeur", { precision: 10, scale: 2 }),
  prixClientFinal: numeric("prix_client_final", { precision: 10, scale: 2 }),
  poidsNet: integer("poids_net"),
  poidsBrut: integer("poids_brut"),
  longueur: integer("longueur"),
  largeur: integer("largeur"),
  favori: boolean("favori").default(false),
  actif: boolean("actif").notNull().default(true),
  noteInterne: text("note_interne"),
  financement24m: numeric("financement_24m", { precision: 10, scale: 2 }),
  financement36m: numeric("financement_36m", { precision: 10, scale: 2 }),
  financement48m: numeric("financement_48m", { precision: 10, scale: 2 }),
  maintenanceMensuelle: numeric("maintenance_mensuelle", { precision: 10, scale: 2 }),
  forfaitDeploiement: numeric("forfait_deploiement", { precision: 10, scale: 2 }),
  // Tarifs de location
  locationJourPartenaire: numeric("location_jour_partenaire", { precision: 10, scale: 2 }),
  locationSemainePartenaire: numeric("location_semaine_partenaire", { precision: 10, scale: 2 }),
  locationMoisPartenaire: numeric("location_mois_partenaire", { precision: 10, scale: 2 }),
  locationJourClient: numeric("location_jour_client", { precision: 10, scale: 2 }),
  locationSemaineClient: numeric("location_semaine_client", { precision: 10, scale: 2 }),
  locationMoisClient: numeric("location_mois_client", { precision: 10, scale: 2 }),
  locationDisponible: boolean("location_disponible").default(false),
  locationDelaiInstallation: text("location_delai_installation"),
  sousGarantie: boolean("sous_garantie").default(false),
  // Champs enrichis robots Pudu
  features: text("features").array(),
  specifications: text("specifications").array(),
  categorieRobot: text("categorie_robot"),
  prixSousDistributeur: numeric("prix_sous_distributeur", { precision: 10, scale: 2 }),
  prixRevendeurN2: numeric("prix_revendeur_n2", { precision: 10, scale: 2 }),
  prixMinPudu: numeric("prix_min_pudu", { precision: 10, scale: 2 }),
  prixAchatConseille: numeric("prix_achat_conseille", { precision: 10, scale: 2 }),
  loc24Mois: numeric("loc_24_mois", { precision: 10, scale: 2 }),
  loc36Mois: numeric("loc_36_mois", { precision: 10, scale: 2 }),
  loc48Mois: numeric("loc_48_mois", { precision: 10, scale: 2 }),
  statut: text("statut").default("actif"),
  // Champs scanner IA
  visualFeatures: text("visual_features"),
  commonConfusions: text("common_confusions").array(),
  scanKeywords: text("scan_keywords").array(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 4.6b extensions_garantie
export const extensionsGarantie = pgTable("extensions_garantie", {
  id: uuid("id").primaryKey().defaultRandom(),
  codeArticle: text("code_article").unique().notNull(),
  productId: uuid("product_id").references(() => products.id),
  robotNom: text("robot_nom").notNull(),
  dureeAns: integer("duree_ans").notNull(),
  prixRevendeur: numeric("prix_revendeur", { precision: 10, scale: 2 }),
  prixClientFinal: numeric("prix_client_final", { precision: 10, scale: 2 }),
  actif: boolean("actif").default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

// 4.7 resellers
export const resellers = pgTable("resellers", {
  id: uuid("id").primaryKey().defaultRandom(),
  companyName: text("company_name").notNull(),
  nomCommercial: text("nom_commercial"),
  siren: text("siren"),
  sector: text("sector"),
  groupe: text("groupe"),
  secteurActivite: text("secteur_activite"),
  anneesPartenariat: integer("annees_partenariat").default(1),
  address: text("address"),
  contactCivility: text("contact_civility", { enum: ["M.", "Mme"] }),
  contactFirstName: text("contact_first_name"),
  contactLastName: text("contact_last_name"),
  contactPhone: text("contact_phone"),
  contactEmail: text("contact_email"),
  contractStatus: text("contract_status", { enum: ["revendeur", "revendeur_premium", "sous_distributeur", "apporteur_affaires", "annule"] }).notNull().default("revendeur"),
  geographicZones: text("geographic_zones"),
  contractStartDate: date("contract_start_date"),
  contractEndDate: date("contract_end_date"),
  annualSalesTarget: integer("annual_sales_target"),
  commercialId: uuid("commercial_id").$type<ProfileId>().references(() => profiles.id),
  resellerNumber: text("reseller_number"),
  puduAgentCode: text("pudu_agent_code"),
  logoUrl: text("logo_url"),
  siteWeb: text("site_web"),
  actif: boolean("actif").notNull().default(true),
  notes: text("notes"),
  couleurPrimaire: text("couleur_primaire").default('#2563eb'),
  couleurSecondaire: text("couleur_secondaire").default('#1e40af'),
  siret: text("siret"),
  numeroTva: text("numero_tva"),
  nbSalaries: integer("nb_salaries"),
  formeJuridique: text("forme_juridique"),
  nomDirigeant: text("nom_dirigeant"),
  zohoAliases: text("zoho_aliases").array(),
  groupeAppartenance: text("groupe_appartenance"),
  revendeurParentId: uuid("revendeur_parent_id"),
  codeCourt: varchar("code_court", { length: 5 }),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  geocodingStatus: text("geocoding_status").default("pending"),
  geocodedAddress: text("geocoded_address"),
  geocodingScore: doublePrecision("geocoding_score"),
  geocodingLabel: text("geocoding_label"),
  departementSiege: text("departement_siege"),
  geocodedAt: timestamp("geocoded_at", { withTimezone: true }),
  // Phase 1 — modèle deux niveaux
  type: revendeurTypeEnum("type").notNull().default("independant"),
  facturationAdresse: text("facturation_adresse"),
  facturationCodePostal: text("facturation_code_postal"),
  facturationVille: text("facturation_ville"),
  facturationPays: text("facturation_pays").default("France"),
  conditionsPaiement: text("conditions_paiement"),
  escompte: text("escompte"),
  conditionsTransport: text("conditions_transport"),
  contactAchatsNom: text("contact_achats_nom"),
  contactAchatsEmail: text("contact_achats_email"),
  contactAchatsTel: text("contact_achats_tel"),
  codeFournisseurChezRevendeur: text("code_fournisseur_chez_revendeur"),
  contratReference: text("contrat_reference"),
  contratSigneLe: date("contrat_signe_le"),
  // ── Onboarding ─────────────────────────────────────────────────────────────
  prenomDirigeant: text("prenom_dirigeant"),
  statutOnboarding: text("statut_onboarding").default("declaration_initiee"),
  codePostal: text("code_postal"),
  ville: text("ville"),
  hubspotStatut: text("hubspot_statut").default("a_faire"),
  hubspotDoneAt: timestamp("hubspot_done_at", { withTimezone: true }),
  hubspotDoneBy: uuid("hubspot_done_by"),
  puduCloudStatut: text("pudu_cloud_statut").default("a_faire"),
  puduCloudDoneAt: timestamp("pudu_cloud_done_at", { withTimezone: true }),
  puduCloudDoneBy: uuid("pudu_cloud_done_by"),
  autorisationStatut: text("autorisation_statut").default("a_faire"),
  autorisationDoneAt: timestamp("autorisation_done_at", { withTimezone: true }),
  autorisationDoneBy: uuid("autorisation_done_by"),
  portailStatut: text("portail_statut").default("a_faire"),
  portailDoneAt: timestamp("portail_done_at", { withTimezone: true }),
  portailDoneBy: uuid("portail_done_by"),
  recapAccesEnvoyeAt: timestamp("recap_acces_envoye_at", { withTimezone: true }),
  // ── Consigne 1 — Couche entité : type de relation + niveau tarifaire ───────
  typeRelation: text("type_relation"),
  // ^ valeurs applicatives : partenaire_direct | sous_revendeur | reseau_externe (dormant)
  niveauTarifaire: text("niveau_tarifaire"),
  // ^ palier grille produit : sous_distributeur_premium | revendeur_niveau_2 — assigné manuellement
  niveauTarifaireSetBy: uuid("niveau_tarifaire_set_by").$type<ProfileId>().references(() => profiles.id),
  niveauTarifaireSetAt: timestamp("niveau_tarifaire_set_at", { withTimezone: true }),
  // ─────────────────────────────────────────────────────────────────────────────
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const revendeurZones = pgTable("revendeur_zones", {
  id: uuid("id").defaultRandom().primaryKey(),
  revendeurId: uuid("revendeur_id").notNull().references(() => resellers.id, { onDelete: "cascade" }),
  departementCode: text("departement_code").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("revendeur_zones_uniq").on(t.revendeurId, t.departementCode),
  byDept: index("revendeur_zones_dept_idx").on(t.departementCode),
}));

export const revendeurSites = pgTable("revendeur_sites", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").references(() => resellers.id, { onDelete: "restrict" }),
  clientId: uuid("client_id").references(() => clients.id, { onDelete: "restrict" }),
  nom: text("nom").notNull(),
  typeSite: siteTypeEnum("type_site").notNull().default("point_de_vente"),
  livraisonAdresse: text("livraison_adresse"),
  livraisonCodePostal: text("livraison_code_postal"),
  livraisonVille: text("livraison_ville"),
  livraisonPays: text("livraison_pays").default("France"),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  geocodeStatut: geocodeStatutPgEnum("geocode_statut").notNull().default("a_faire"),
  geocodeScore: doublePrecision("geocode_score"),
  contactReceptionNom: text("contact_reception_nom"),
  contactReceptionEmail: text("contact_reception_email"),
  contactReceptionTel: text("contact_reception_tel"),
  livraisonHoraires: text("livraison_horaires"),
  livraisonRdvObligatoire: boolean("livraison_rdv_obligatoire").notNull().default(false),
  livraisonSeuilPalettesRdv: integer("livraison_seuil_palettes_rdv"),
  livraisonRdvEmail: text("livraison_rdv_email"),
  livraisonNotes: text("livraison_notes"),
  joursPreference: text("jours_preference").array().default(sql`'{}'`),
  modeLivraison: text("mode_livraison").default("livraison_adresse"),
  estSiteParDefaut: boolean("est_site_par_defaut").notNull().default(false),
  accesActif: boolean("acces_actif").notNull().default(false),
  actif: boolean("actif").notNull().default(true),
  siret: text("siret"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxRevendeur: index("idx_revendeur_sites_revendeur").on(t.revendeurId),
  idxType: index("idx_revendeur_sites_type").on(t.typeSite),
  uniqSiret: uniqueIndex("uniq_revendeur_sites_siret").on(t.siret).where(sql`siret IS NOT NULL`),
}));

export type RevendeurSite = typeof revendeurSites.$inferSelect;
export type InsertRevendeurSite = typeof revendeurSites.$inferInsert;

export const revendeurDocuments = pgTable("revendeur_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").references(() => resellers.id).notNull(),
  nom: text("nom").notNull(),
  type: text("type", { enum: ["contrat", "autorisation", "diplome", "accord", "autre"] }).notNull().default("autre"),
  fichierUrl: text("fichier_url").notNull(),
  dateUpload: date("date_upload").defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const revendeurContrats = pgTable("revendeur_contrats", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").references(() => resellers.id).notNull(),
  annee: integer("annee").notNull(),
  label: text("label"),
  dateDebut: date("date_debut"),
  dateFin: date("date_fin"),
  statut: text("statut"),
  notes: text("notes"),
  objectifAnnuel: integer("objectif_annuel").default(0),
  totalRobotsVendus: integer("total_robots_vendus").default(0),
  tauxAtteinte: numeric("taux_atteinte", { precision: 5, scale: 2 }),
  apporteurAffaires: boolean("apporteur_affaires").default(false),
  contratExecution: boolean("contrat_execution").default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

// 5.1 missions
export const missions = pgTable("missions", {
  id: uuid("id").primaryKey().defaultRandom(),
  missionType: text("mission_type", { enum: ["demonstration", "cdc", "installation", "intervention_sav", "maintenance", "formation", "atelier", "location", "livraison", "trajet", "salon", "salon_interne", "salon_partenaire", "prestation_evenement_client", "bureau", "organisation"] }).notNull(),
  status: text("status", { enum: ["option", "confirme", "annule", "realisee"] }).notNull().default("option"),
  clientId: uuid("client_id").references(() => clients.id),
  prospectId: uuid("prospect_id").references(() => prospects.id),
  tiersType: text("tiers_type"),
  tiersId: uuid("tiers_id"),
  commercialId: uuid("commercial_id").$type<ProfileId>().references(() => profiles.id),
  startDate: timestamp("start_date", { withTimezone: true }).notNull(),
  endDate: timestamp("end_date", { withTimezone: true }).notNull(),
  heureDebut: text("heure_debut"),
  heureFin: text("heure_fin"),
  arrivalTime: text("arrival_time"),
  demoStartTime: text("demo_start_time"),
  eventName: text("event_name"),
  evenementNom: text("evenement_nom"),
  phase: text("phase", { enum: ["montage", "presence", "demontage"] }),
  description: text("description"),
  accessInfo: text("access_info"),
  createdBy: uuid("created_by").$type<ProfileId>().references(() => profiles.id).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  // Lien sous-traitance partenaires
  prestationId: uuid("prestation_id"),
  // Couche calendrier (refonte volet 1)
  dateFin: date("date_fin"),
  journeeEntiere: boolean("journee_entiere").notNull().default(false),
  lieuLibre: text("lieu_libre"),
  lienVisio: text("lien_visio"),
  // Trajet (déplacement)
  adresseDepart: text("adresse_depart"),
  departLat: doublePrecision("depart_lat"),
  departLng: doublePrecision("depart_lng"),
  adresseArrivee: text("adresse_arrivee"),
  ariveeLat: doublePrecision("arrivee_lat"),
  ariveeLng: doublePrecision("arrivee_lng"),
  dureeTrajetMinutes: integer("duree_trajet_minutes"),
  distanceTrajetKm: numeric("distance_trajet_km"),
  trajetCalculeAt: timestamp("trajet_calcule_at", { withTimezone: true }),
  trajetErreur: text("trajet_erreur"),
  googleEventId: text("google_event_id"),
  googleSyncedAt: timestamp("google_synced_at", { withTimezone: true }),
  googleSyncStatus: text("google_sync_status", { enum: ["synced", "error"] }),
  googleSyncError: text("google_sync_error"),
});

// 5.2 mission_technicians
export const missionTechnicians = pgTable("mission_technicians", {
  missionId: uuid("mission_id").references(() => missions.id).notNull(),
  technicianId: uuid("technician_id").$type<ProfileId>().references(() => profiles.id).notNull(),
  isRemote: boolean("is_remote").default(false),
});

// 5.3 mission_robots
export const missionRobots = pgTable("mission_robots", {
  missionId: uuid("mission_id").references(() => missions.id).notNull(),
  robotId: uuid("robot_id").references(() => robots.id).notNull(),
  departurePhotos: text("departure_photos").array(),
  returnPhotos: text("return_photos").array(),
  departureConfirmedAt: timestamp("departure_confirmed_at", { withTimezone: true }),
  returnConfirmedAt: timestamp("return_confirmed_at", { withTimezone: true }),
});

export const missionRobotsClients = pgTable("mission_robots_clients", {
  id: uuid("id").primaryKey().defaultRandom(),
  missionId: uuid("mission_id").references(() => missions.id, { onDelete: "cascade" }).notNull(),
  robotClientId: uuid("robot_client_id").references(() => robotsClients.id).notNull(),
  motif: text("motif"),
  remplacementRequis: boolean("remplacement_requis").notNull().default(false),
  robotRemplacementId: uuid("robot_remplacement_id").references(() => robots.id),
  robotRemplacementSn: text("robot_remplacement_sn"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  missionRobotClientUnique: unique("mission_robots_clients_mission_robot_unique").on(table.missionId, table.robotClientId),
  replacementExclusive: check(
    "mission_robots_clients_replacement_exclusive",
    sql`(${table.remplacementRequis} OR (${table.robotRemplacementId} IS NULL AND ${table.robotRemplacementSn} IS NULL))
      AND NOT (${table.robotRemplacementId} IS NOT NULL AND ${table.robotRemplacementSn} IS NOT NULL)`,
  ),
}));

// 5.4 mission_vehicles
export const missionVehicles = pgTable("mission_vehicles", {
  missionId: uuid("mission_id").references(() => missions.id).notNull(),
  vehicleId: uuid("vehicle_id").references(() => vehicles.id).notNull(),
});

// 5.5 mission_commercials
export const missionCommercials = pgTable("mission_commercials", {
  missionId: uuid("mission_id").references(() => missions.id).notNull(),
  commercialId: uuid("commercial_id").$type<ProfileId>().references(() => profiles.id).notNull(),
});

// 5.6 mission_segments (planning détaillé pour Installation/Démonstration)
export const missionSegments = pgTable("mission_segments", {
  id: uuid("id").primaryKey().defaultRandom(),
  missionId: uuid("mission_id").references(() => missions.id).notNull(),
  type: text("type", { enum: ["intervention", "trajet_aller", "trajet_retour", "nuit_hotel", "formation", "deplacement_partiel"] }).notNull(),
  date: date("date").notNull(),
  heureDebut: text("heure_debut"),
  heureFin: text("heure_fin"),
  label: text("label"),
  techniciensIds: text("techniciens_ids").array(),
  vehiculeId: uuid("vehicule_id").references(() => vehicles.id),
  notes: text("notes"),
  ordre: integer("ordre").notNull().default(0),
});

// 5.7 tasks (Suivi des actions)
export const emetteurTypeEnum = z.enum(["client", "revendeur", "prospect", "libre", "equipe"]);

export const tasks = pgTable("tasks", {
  id: uuid("id").primaryKey().defaultRandom(),
  titre: text("titre").notNull(),
  taskType: text("task_type", { enum: ["operationnelle", "personnelle"] }).notNull().default("operationnelle"),
  emetteur: text("emetteur"),
  emetteurLabel: text("emetteur_label"),
  emetteurId: uuid("emetteur_id"),
  emetteurType: text("emetteur_type", { enum: ["client", "revendeur", "prospect", "libre", "equipe"] }),
  assigneeId: uuid("assignee_id").$type<ProfileId>().references(() => profiles.id),
  assigneeType: text("assignee_type", { enum: ["technicien", "commercial", "autre"] }),
  labels: text("labels").array(),
  priorite: text("priorite", { enum: ["1", "2", "3"] }).notNull().default("2"),
  status: text("status", { enum: ["a_faire", "en_cours", "fait"] }).notNull().default("a_faire"),
  dateEcheance: date("date_echeance"),
  rappelDate: timestamp("rappel_date", { withTimezone: true }),
  rappelNote: text("rappel_note"),
  notes: text("notes"),
  missionId: uuid("mission_id").references(() => missions.id),
  archive: boolean("archive").default(false),
  dateArchivage: timestamp("date_archivage", { withTimezone: true }),
  createdBy: uuid("created_by").$type<ProfileId>().references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  isDraft: boolean("is_draft").default(false).notNull(),
});

// 5.6c mission_documents (pièces jointes de mission)
export const missionDocuments = pgTable("mission_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  missionId: uuid("mission_id").notNull().references(() => missions.id, { onDelete: "cascade" }),
  nomFichier: text("nom_fichier").notNull(),
  cheminStorage: text("chemin_storage").notNull(),
  typeMime: text("type_mime").notNull(),
  tailleOctets: bigint("taille_octets", { mode: "number" }).notNull(),
  categorie: text("categorie").notNull().default("autre"),
  description: text("description"),
  uploadedBy: uuid("uploaded_by").references(() => profiles.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  missionIdIdx: index("mission_documents_mission_id_idx").on(table.missionId),
}));
export type MissionDocument = typeof missionDocuments.$inferSelect;

// 5.6b action_attachments
export const actionAttachments = pgTable("action_attachments", {
  id: uuid("id").primaryKey().defaultRandom(),
  actionId: uuid("action_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  storagePath: text("storage_path").notNull(),
  originalFilename: text("original_filename").notNull(),
  mimeType: text("mime_type").notNull(),
  sizeBytes: integer("size_bytes").notNull(),
  uploadedBy: text("uploaded_by").notNull(),
  uploadedAt: timestamp("uploaded_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => ({
  actionIdIdx: index("action_attachments_action_id_idx").on(table.actionId),
}));

export type ActionAttachment = typeof actionAttachments.$inferSelect;

// 5.7 task_comments
export const taskComments = pgTable("task_comments", {
  id: uuid("id").primaryKey().defaultRandom(),
  taskId: uuid("task_id").references(() => tasks.id).notNull(),
  auteur: text("auteur").notNull(),
  contenu: text("contenu").notNull(),
  pieceJointeUrl: text("piece_jointe_url"),
  isSystem: boolean("is_system").default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

// 5.8 task_pieces (pièces en relation)
export const taskPieces = pgTable("task_pieces", {
  id: uuid("id").primaryKey().defaultRandom(),
  taskId: uuid("task_id").references(() => tasks.id).notNull(),
  produitId: uuid("produit_id").references(() => products.id).notNull(),
  note: text("note"),
});

// 7.1 tickets
export const tickets = pgTable("tickets", {
  id: uuid("id").primaryKey().defaultRandom(),
  ticketNumber: text("ticket_number").unique().notNull(),
  receivedAt: timestamp("received_at", { withTimezone: true }).defaultNow(),
  clientId: uuid("client_id").references(() => clients.id),
  resellerId: uuid("reseller_id").references(() => resellers.id),
  contactName: text("contact_name"),
  contactPhone: text("contact_phone"),
  contactEmail: text("contact_email"),
  // Nommage historique : `titre` est l'objet métier libre saisi par l'utilisateur.
  titre: text("titre"),
  source: text("source", { enum: ["formulaire", "email", "telephone", "chat", "autre", "courrier", "portail_partenaire"] }).notNull().default("formulaire"),
  priority: text("priority", { enum: ["faible", "moyen", "eleve", "urgent"] }).notNull().default("moyen"),
  status: text("status", { enum: ["nouveau", "en_cours", "en_attente_piece", "resolu", "ferme"] }).notNull().default("nouveau"),
  assignedTo: uuid("assigned_to").$type<ProfileId>().references(() => profiles.id),
  robotId: uuid("robot_id").references(() => robots.id),
  problemDescription: text("problem_description"),
  hubspotId: text("hubspot_id"),
  puduWarrantyActive: boolean("pudu_warranty_active").default(false),
  dateResolution: timestamp("date_resolution", { withTimezone: true }),
  ancienNumero: text("ancien_numero"),
  creeParId: uuid("cree_par_id").$type<ProfileId>().references(() => profiles.id),
  creeParType: text("cree_par_type", { enum: ["admin", "technicien", "commercial"] }),
  deplacementTechnicien: boolean("deplacement_technicien").default(false),
  archive: boolean("archive").default(false),
  dateArchivage: timestamp("date_archivage", { withTimezone: true }),
  createdBy: uuid("created_by").$type<ProfileId>().references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  resumeIa: text("resume_ia"),
  resumeIaDate: timestamp("resume_ia_date", { withTimezone: true }),
  // Champs portail partenaire
  serialNumberFree: text("serial_number_free"),
  datePanne: date("date_panne"),
  videoUrl: text("video_url"),
  clientFinalNom: text("client_final_nom"),
  livraisonNomEntreprise: text("livraison_nom_entreprise"),
  livraisonAdresse: text("livraison_adresse"),
  livraisonContact: text("livraison_contact"),
  livraisonPhone: text("livraison_phone"),
  // Lien chat origine
  chatOrigineRef: text("chat_origine_ref"),
  chatOrigineId: uuid("chat_origine_id"),
  // Lien sous-traitance partenaires
  prestationId: uuid("prestation_id"),
  // Lien robot parc déployé Pudu
  puduRobotId: uuid("pudu_robot_id").references(() => puduRobots.id),
  // Work Order Pudu (saisie manuelle, format DBWO-XXXXXXXXXXXX)
  puduWorkOrder: text("pudu_work_order"),
  // Threading mail SAV — vrai Message-ID RFC Gmail (<…@mail.gmail.com>) pour In-Reply-To
  emailThreadId: text("email_thread_id"),
  // Threading mail SAV — threadId opaque Gmail pour rejoindre le fil côté envoi
  gmailThreadId: text("gmail_thread_id"),
  // Fil Gmail distinct réservé aux échanges client déclenchés depuis le ticket
  clientThreadId: text("client_thread_id"),
  // Nommage historique : `objet` est la référence formatée, générée et figée à la création.
  objet: text("objet"),
});

// 7.2 ticket_notes
export const ticketNotes = pgTable("ticket_notes", {
  id: uuid("id").primaryKey().defaultRandom(),
  ticketId: uuid("ticket_id").references(() => tickets.id).notNull(),
  content: text("content").notNull(),
  authorId: uuid("author_id").$type<ProfileId>().references(() => profiles.id).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

// 7.3 ticket_missions
export const ticketMissions = pgTable("ticket_missions", {
  ticketId: uuid("ticket_id").references(() => tickets.id).notNull(),
  missionId: uuid("mission_id").references(() => missions.id).notNull(),
});

// 7.4 ticket_messages (discussion thread)
export const ticketMessages = pgTable("ticket_messages", {
  id: uuid("id").primaryKey().defaultRandom(),
  ticketId: uuid("ticket_id").references(() => tickets.id).notNull(),
  auteur: text("auteur").notNull(),
  auteurId: uuid("auteur_id").$type<ProfileId>().references(() => profiles.id),
  contenu: text("contenu").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  direction: text("direction"),
  source: text("source"),
  sourceMessageId: text("source_message_id"),
  mentionedUserIds: text("mentioned_user_ids").array(),
  editedAt: timestamp("edited_at", { withTimezone: true }),
}, (t) => ({
  sourceMessageIdUnique: uniqueIndex("ticket_messages_source_msg_uniq")
    .on(t.sourceMessageId)
    .where(sql`${t.sourceMessageId} IS NOT NULL`),
}));

export const ticketMessagesEmail = pgTable("ticket_messages_email", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  ticketMessageId: uuid("ticket_message_id")
    .notNull()
    .unique()
    .references(() => ticketMessages.id, { onDelete: "cascade" }),
  modeleCode: text("modele_code"),
  objet: text("objet").notNull(),
  destinataire: text("destinataire").notNull(),
  expediteur: text("expediteur").notNull(),
  nbPiecesJointes: integer("nb_pieces_jointes").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});

export const gmailMailboxCursor = pgTable("gmail_mailbox_cursor", {
  id: text("id").primaryKey().default("main"),
  historyId: text("history_id").notNull(),
  activatedAt: timestamp("activated_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  verrouJusquA: timestamp("verrou_jusqu_a", { withTimezone: true }),
  fallbackActive: boolean("fallback_active").notNull().default(false),
  fallbackAfterEpoch: bigint("fallback_after_epoch", { mode: "number" }),
  fallbackBeforeEpoch: bigint("fallback_before_epoch", { mode: "number" }),
  fallbackPageToken: text("fallback_page_token"),
  fallbackAnchorHistoryId: text("fallback_anchor_history_id"),
});

// 7.5 ticket_pieces
export const ticketPieces = pgTable("ticket_pieces", {
  id: uuid("id").primaryKey().defaultRandom(),
  ticketId: uuid("ticket_id").references(() => tickets.id).notNull(),
  produitId: uuid("produit_id").references(() => products.id).notNull(),
  quantite: integer("quantite").default(1).notNull(),
  sousGarantie: boolean("sous_garantie").default(false),
  note: text("note"),
  puduMaterialRequest: text("pudu_material_request"),
  trackingNumber: text("tracking_number"),
  dateReceptionPrevue: date("date_reception_prevue"),
  destinataire: text("destinataire"),
  receptionNotifiee: boolean("reception_notifiee").default(false).notNull(),
  adresseLivraison: text("adresse_livraison"),
});

// 7.6 ticket_media
export const ticketMedia = pgTable("ticket_media", {
  id: uuid("id").primaryKey().defaultRandom(),
  ticketId: uuid("ticket_id").references(() => tickets.id).notNull(),
  storagePath: text("storage_path").notNull(),
  mediaType: text("media_type").notNull(), // 'image' | 'video' | 'document'
  mimeType: text("mime_type"),
  originalFilename: text("original_filename"),
  sizeBytes: bigint("size_bytes", { mode: "number" }),
  uploadedBy: uuid("uploaded_by").$type<ProfileId>().references(() => profiles.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Colonnes ingestion Gmail (Phase 2)
  source: text("source").notNull().default("manual"), // 'manual' | 'email'
  emailFrom: text("email_from"),
  gmailMessageId: text("gmail_message_id"),
  gmailAttachmentId: text("gmail_attachment_id"),
  contentSha256: text("content_sha256"),
});

export const insertTicketMediaSchema = createInsertSchema(ticketMedia).omit({ id: true, createdAt: true });
export type InsertTicketMedia = z.infer<typeof insertTicketMediaSchema>;
export type TicketMediaItem = typeof ticketMedia.$inferSelect;

// 6.1 robot_locations
export const robotLocations = pgTable("robot_locations", {
  id: uuid("id").primaryKey().defaultRandom(),
  robotId: uuid("robot_id").references(() => robots.id).notNull(),
  locationType: text("location_type", { enum: ["mission", "client", "atelier", "pret", "sav"] }).notNull(),
  clientId: uuid("client_id").references(() => clients.id),
  missionId: uuid("mission_id").references(() => missions.id, { onDelete: "set null" }),
  locationDescription: text("location_description"),
  startedAt: timestamp("started_at", { withTimezone: true }).notNull().defaultNow(),
  endedAt: timestamp("ended_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

// 6.2 robot_events
export const robotEvents = pgTable("robot_events", {
  id: uuid("id").primaryKey().defaultRandom(),
  robotId: uuid("robot_id").references(() => robots.id, { onDelete: "cascade" }).notNull(),
  type: text("type").notNull(),
  ancienStatut: text("ancien_statut"),
  nouveauStatut: text("nouveau_statut"),
  provenance: text("provenance"),
  note: text("note"),
  photos: text("photos").array(),
  userId: uuid("user_id"),
  userName: text("user_name"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type RobotEvent = typeof robotEvents.$inferSelect;

// --- Relations ---

export const agencesRelations = relations(agences, ({ many }) => ({
  profiles: many(profiles),
}));

export const profilesRelations = relations(profiles, ({ one, many }) => ({
  agence: one(agences, {
    fields: [profiles.agenceId],
    references: [agences.id],
  }),
  schedules: many(technicianSchedules),
  clients: many(clients),
  resellers: many(resellers),
  createdMissions: many(missions, { relationName: "missionCreator" }),
  commercialMissions: many(missions, { relationName: "missionCommercial" }),
  missionTechnicians: many(missionTechnicians),
  missionCommercials: many(missionCommercials),
  assignedTasks: many(tasks, { relationName: "taskAssignee" }),
  createdTasks: many(tasks, { relationName: "taskCreator" }),
  assignedTickets: many(tickets, { relationName: "ticketAssignee" }),
  createdTickets: many(tickets, { relationName: "ticketCreator" }),
  ticketNotes: many(ticketNotes),
}));

export const technicianSchedulesRelations = relations(technicianSchedules, ({ one }) => ({
  technician: one(profiles, {
    fields: [technicianSchedules.technicianId],
    references: [profiles.id],
  }),
}));

export const robotsRelations = relations(robots, ({ many }) => ({
  missionRobots: many(missionRobots),
  missionRobotsClients: many(missionRobotsClients),
  locations: many(robotLocations),
  tickets: many(tickets),
}));

export const clientsRelations = relations(clients, ({ one, many }) => ({
  commercial: one(profiles, {
    fields: [clients.commercialId],
    references: [profiles.id],
  }),
  missions: many(missions),
  tickets: many(tickets),
}));

export const vehiclesRelations = relations(vehicles, ({ many }) => ({
  missionVehicles: many(missionVehicles),
}));

export const resellersRelations = relations(resellers, ({ one, many }) => ({
  commercial: one(profiles, {
    fields: [resellers.commercialId],
    references: [profiles.id],
  }),
  tickets: many(tickets),
  documents: many(revendeurDocuments),
  contrats: many(revendeurContrats),
  robots: many(robotsClients),
}));

export const revendeurDocumentsRelations = relations(revendeurDocuments, ({ one }) => ({
  reseller: one(resellers, { fields: [revendeurDocuments.revendeurId], references: [resellers.id] }),
}));

export const revendeurContratsRelations = relations(revendeurContrats, ({ one }) => ({
  reseller: one(resellers, { fields: [revendeurContrats.revendeurId], references: [resellers.id] }),
}));

export const missionsRelations = relations(missions, ({ one, many }) => ({
  client: one(clients, {
    fields: [missions.clientId],
    references: [clients.id],
  }),
  commercial: one(profiles, {
    fields: [missions.commercialId],
    references: [profiles.id],
    relationName: "missionCommercial",
  }),
  creator: one(profiles, {
    fields: [missions.createdBy],
    references: [profiles.id],
    relationName: "missionCreator",
  }),
  missionTechnicians: many(missionTechnicians),
  missionRobots: many(missionRobots),
  missionVehicles: many(missionVehicles),
  missionCommercials: many(missionCommercials),
  missionSegments: many(missionSegments),
  tasks: many(tasks),
  ticketMissions: many(ticketMissions),
}));

export const missionTechniciansRelations = relations(missionTechnicians, ({ one }) => ({
  mission: one(missions, {
    fields: [missionTechnicians.missionId],
    references: [missions.id],
  }),
  technician: one(profiles, {
    fields: [missionTechnicians.technicianId],
    references: [profiles.id],
  }),
}));

export const missionRobotsRelations = relations(missionRobots, ({ one }) => ({
  mission: one(missions, {
    fields: [missionRobots.missionId],
    references: [missions.id],
  }),
  robot: one(robots, {
    fields: [missionRobots.robotId],
    references: [robots.id],
  }),
}));

export const missionRobotsClientsRelations = relations(missionRobotsClients, ({ one }) => ({
  mission: one(missions, { fields: [missionRobotsClients.missionId], references: [missions.id] }),
  robotClient: one(robotsClients, { fields: [missionRobotsClients.robotClientId], references: [robotsClients.id] }),
  replacementRobot: one(robots, { fields: [missionRobotsClients.robotRemplacementId], references: [robots.id] }),
}));

export const missionVehiclesRelations = relations(missionVehicles, ({ one }) => ({
  mission: one(missions, {
    fields: [missionVehicles.missionId],
    references: [missions.id],
  }),
  vehicle: one(vehicles, {
    fields: [missionVehicles.vehicleId],
    references: [vehicles.id],
  }),
}));

export const missionCommercialsRelations = relations(missionCommercials, ({ one }) => ({
  mission: one(missions, {
    fields: [missionCommercials.missionId],
    references: [missions.id],
  }),
  commercial: one(profiles, {
    fields: [missionCommercials.commercialId],
    references: [profiles.id],
  }),
}));

export const missionSegmentsRelations = relations(missionSegments, ({ one }) => ({
  mission: one(missions, {
    fields: [missionSegments.missionId],
    references: [missions.id],
  }),
  vehicule: one(vehicles, {
    fields: [missionSegments.vehiculeId],
    references: [vehicles.id],
  }),
}));

export const robotLocationsRelations = relations(robotLocations, ({ one }) => ({
  robot: one(robots, {
    fields: [robotLocations.robotId],
    references: [robots.id],
  }),
  client: one(clients, {
    fields: [robotLocations.clientId],
    references: [clients.id],
  }),
  mission: one(missions, {
    fields: [robotLocations.missionId],
    references: [missions.id],
  }),
}));

export const tasksRelations = relations(tasks, ({ one, many }) => ({
  assignee: one(profiles, {
    fields: [tasks.assigneeId],
    references: [profiles.id],
    relationName: "taskAssignee",
  }),
  creator: one(profiles, {
    fields: [tasks.createdBy],
    references: [profiles.id],
    relationName: "taskCreator",
  }),
  mission: one(missions, {
    fields: [tasks.missionId],
    references: [missions.id],
  }),
  comments: many(taskComments),
  pieces: many(taskPieces),
}));

export const taskCommentsRelations = relations(taskComments, ({ one }) => ({
  task: one(tasks, {
    fields: [taskComments.taskId],
    references: [tasks.id],
  }),
}));

export const taskPiecesRelations = relations(taskPieces, ({ one }) => ({
  task: one(tasks, {
    fields: [taskPieces.taskId],
    references: [tasks.id],
  }),
  produit: one(products, {
    fields: [taskPieces.produitId],
    references: [products.id],
  }),
}));

export const ticketsRelations = relations(tickets, ({ one, many }) => ({
  client: one(clients, {
    fields: [tickets.clientId],
    references: [clients.id],
  }),
  reseller: one(resellers, {
    fields: [tickets.resellerId],
    references: [resellers.id],
  }),
  assignee: one(profiles, {
    fields: [tickets.assignedTo],
    references: [profiles.id],
    relationName: "ticketAssignee",
  }),
  creator: one(profiles, {
    fields: [tickets.createdBy],
    references: [profiles.id],
    relationName: "ticketCreator",
  }),
  robot: one(robots, {
    fields: [tickets.robotId],
    references: [robots.id],
  }),
  creeParProfile: one(profiles, {
    fields: [tickets.creeParId],
    references: [profiles.id],
    relationName: "ticketCreeParProfile",
  }),
  notes: many(ticketNotes),
  ticketMissions: many(ticketMissions),
  messages: many(ticketMessages),
  pieces: many(ticketPieces),
}));

export const ticketNotesRelations = relations(ticketNotes, ({ one }) => ({
  ticket: one(tickets, {
    fields: [ticketNotes.ticketId],
    references: [tickets.id],
  }),
  author: one(profiles, {
    fields: [ticketNotes.authorId],
    references: [profiles.id],
  }),
}));

export const ticketMissionsRelations = relations(ticketMissions, ({ one }) => ({
  ticket: one(tickets, {
    fields: [ticketMissions.ticketId],
    references: [tickets.id],
  }),
  mission: one(missions, {
    fields: [ticketMissions.missionId],
    references: [missions.id],
  }),
}));

export const ticketMessagesRelations = relations(ticketMessages, ({ one }) => ({
  ticket: one(tickets, {
    fields: [ticketMessages.ticketId],
    references: [tickets.id],
  }),
  auteurProfile: one(profiles, {
    fields: [ticketMessages.auteurId],
    references: [profiles.id],
  }),
  emailMetadata: one(ticketMessagesEmail, {
    fields: [ticketMessages.id],
    references: [ticketMessagesEmail.ticketMessageId],
  }),
}));

export const ticketMessagesEmailRelations = relations(ticketMessagesEmail, ({ one }) => ({
  message: one(ticketMessages, {
    fields: [ticketMessagesEmail.ticketMessageId],
    references: [ticketMessages.id],
  }),
}));

export const ticketPiecesRelations = relations(ticketPieces, ({ one }) => ({
  ticket: one(tickets, {
    fields: [ticketPieces.ticketId],
    references: [tickets.id],
  }),
  produit: one(products, {
    fields: [ticketPieces.produitId],
    references: [products.id],
  }),
}));

// 8. Robots Clients (client-owned robots, distinct from demo fleet)
export const robotsClients = pgTable("robots_clients", {
  id: uuid("id").primaryKey().defaultRandom(),
  clientId: uuid("client_id").references(() => clients.id),
  resellerId: uuid("reseller_id").references(() => resellers.id),
  modele: text("modele").notNull(),
  numeroSerie: text("numero_serie").unique().notNull(),
  macAddress: text("mac_address"),
  dateAchat: date("date_achat"),
  dateInstallation: date("date_installation"),
  garantieFin: date("garantie_fin"),
  statut: text("statut", {
    enum: ROBOT_CLIENT_STATUSES.map((status) => status.value) as [
      "actif",
      "en_reparation",
      "hors_service",
    ],
  }).notNull().default("actif"),
  imageUrl: text("image_url"),
  profilePhotoUrl: text("profile_photo_url"),
  photoFrontUrl: text("photo_front_url"),
  photoBackUrl: text("photo_back_url"),
  photoLeftUrl: text("photo_left_url"),
  photoRightUrl: text("photo_right_url"),
  notes: text("notes"),
  aCompleter: boolean("a_completer").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const robotsClientsRelations = relations(robotsClients, ({ one, many }) => ({
  client: one(clients, { fields: [robotsClients.clientId], references: [clients.id] }),
  reseller: one(resellers, { fields: [robotsClients.resellerId], references: [resellers.id] }),
}));

// 9. SAV Module
export const savDossiers = pgTable("sav_dossiers", {
  id: uuid("id").primaryKey().defaultRandom(),
  reference: text("reference").unique().notNull(),
  robotId: uuid("robot_id").references(() => robots.id),
  robotExterne: jsonb("robot_externe"),
  robotClientId: uuid("robot_client_id").references(() => robotsClients.id),
  origine: text("origine", { enum: ["client", "demo"] }).notNull(),
  clientId: uuid("client_id").references(() => clients.id),
  garantie: boolean("garantie").default(false),
  problematique: text("problematique").notNull(),
  priorite: text("priorite", { enum: ["faible", "moyenne", "elevee"] }).notNull().default("moyenne"),
  statut: text("statut", { enum: ["ouvert", "en_cours", "en_attente_pieces", "resolu", "cloture"] }).notNull().default("ouvert"),
  dateCreation: date("date_creation").notNull(),
  dateFinPrevisionnelle: date("date_fin_previsionnelle"),
  dateResolution: date("date_resolution"),
  technicienReferentId: uuid("technicien_referent_id").$type<ProfileId>().references(() => profiles.id),
  photosReception: text("photos_reception").array(),
  notesGenerales: text("notes_generales"),
  numeroSuiviTransporteur: text("numero_suivi_transporteur"),
  retraitNecessaire: boolean("retrait_necessaire").default(false),
  deplacementTechnicien: boolean("deplacement_technicien").default(false),
  robotRemplacementId: uuid("robot_remplacement_id").references(() => robots.id),
  technicienPartenaire: boolean("technicien_partenaire").default(false),
  demandePlanification: boolean("demande_planification").default(false),
  planificationTaskId: uuid("planification_task_id").references(() => tasks.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  resumeIa: text("resume_ia"),
  resumeIaDate: timestamp("resume_ia_date", { withTimezone: true }),
});

export const savInterventions = pgTable("sav_interventions", {
  id: uuid("id").primaryKey().defaultRandom(),
  dossierId: uuid("dossier_id").references(() => savDossiers.id).notNull(),
  technicienId: uuid("technicien_id").$type<ProfileId>().references(() => profiles.id).notNull(),
  dateHeure: timestamp("date_heure", { withTimezone: true }).defaultNow().notNull(),
  description: text("description").notNull(),
  photos: text("photos").array(),
  vocalUrl: text("vocal_url"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const savPieces = pgTable("sav_pieces", {
  id: uuid("id").primaryKey().defaultRandom(),
  dossierId: uuid("dossier_id").references(() => savDossiers.id).notNull(),
  produitId: uuid("produit_id").references(() => products.id).notNull(),
  quantite: integer("quantite").default(1).notNull(),
  statut: text("statut", { enum: ["a_commander", "commandee", "recue", "posee"] }).notNull().default("a_commander"),
  note: text("note"),
});

export const savDossiersRelations = relations(savDossiers, ({ one, many }) => ({
  robot: one(robots, { fields: [savDossiers.robotId], references: [robots.id] }),
  robotClient: one(robotsClients, { fields: [savDossiers.robotClientId], references: [robotsClients.id] }),
  client: one(clients, { fields: [savDossiers.clientId], references: [clients.id] }),
  technicienReferent: one(profiles, { fields: [savDossiers.technicienReferentId], references: [profiles.id] }),
  interventions: many(savInterventions),
  pieces: many(savPieces),
}));

export const savInterventionsRelations = relations(savInterventions, ({ one }) => ({
  dossier: one(savDossiers, { fields: [savInterventions.dossierId], references: [savDossiers.id] }),
  technicien: one(profiles, { fields: [savInterventions.technicienId], references: [profiles.id] }),
}));

export const savPiecesRelations = relations(savPieces, ({ one }) => ({
  dossier: one(savDossiers, { fields: [savPieces.dossierId], references: [savDossiers.id] }),
  produit: one(products, { fields: [savPieces.produitId], references: [products.id] }),
}));

// --- Schemas & Types ---

export const insertProfileSchema = createInsertSchema(profiles, {
  invitedAt: zDateNullable,
}).omit({ createdAt: true, updatedAt: true });
export type Profile = typeof profiles.$inferSelect;
export type InsertProfile = z.infer<typeof insertProfileSchema>;

export const insertAgenceSchema = createInsertSchema(agences).omit({ id: true, createdAt: true });
export type Agence = typeof agences.$inferSelect;
export type InsertAgence = z.infer<typeof insertAgenceSchema>;

export const insertTechnicianScheduleSchema = createInsertSchema(technicianSchedules).omit({ id: true, createdAt: true, updatedAt: true });
export type TechnicianSchedule = typeof technicianSchedules.$inferSelect;
export type InsertTechnicianSchedule = z.infer<typeof insertTechnicianScheduleSchema>;

export const insertRobotSchema = createInsertSchema(robots, {
  statutChangedAt: zDateNullable,
}).omit({ id: true, createdAt: true, updatedAt: true });
export type Robot = typeof robots.$inferSelect;
export type InsertRobot = z.infer<typeof insertRobotSchema>;

export const insertClientSchema = createInsertSchema(clients, {
  emailBienvenueEnvoyeAt: zDateNullable,
  venteDeclareAt: zDateNullable,
  declarationTokenExpiresAt: zDateNullable,
  declarationLienEnvoyeAt: zDateNullable,
  declarationDerniereRelanceAt: zDateNullable,
  geocodedAt: zDateNullable,
}).omit({ id: true, createdAt: true, updatedAt: true });
export type Client = typeof clients.$inferSelect;
export type InsertClient = z.infer<typeof insertClientSchema>;

export const insertProspectSchema = createInsertSchema(prospects).omit({ id: true, createdAt: true, updatedAt: true });
export type Prospect = typeof prospects.$inferSelect;
export type InsertProspect = z.infer<typeof insertProspectSchema>;

export const insertVehicleSchema = createInsertSchema(vehicles)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({ licensePlate: z.string().trim().optional() });
export type Vehicle = typeof vehicles.$inferSelect;
export type InsertVehicle = z.infer<typeof insertVehicleSchema>;

export const insertProductSchema = createInsertSchema(products).omit({ id: true, createdAt: true, updatedAt: true });
export type Product = typeof products.$inferSelect;
export type InsertProduct = z.infer<typeof insertProductSchema>;

export const insertResellerSchema = createInsertSchema(resellers, {
  geocodedAt: zDateNullable,
  hubspotDoneAt: zDateNullable,
  puduCloudDoneAt: zDateNullable,
  autorisationDoneAt: zDateNullable,
  portailDoneAt: zDateNullable,
  recapAccesEnvoyeAt: zDateNullable,
  niveauTarifaireSetAt: zDateNullable,
}).omit({ id: true, createdAt: true, updatedAt: true, groupeAppartenance: true });
export type Reseller = typeof resellers.$inferSelect;
export type InsertReseller = z.infer<typeof insertResellerSchema>;

export const insertMissionSchema = createInsertSchema(missions, {
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  trajetCalculeAt: zDateNullable,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  googleEventId: true,
  googleSyncedAt: true,
  googleSyncStatus: true,
  googleSyncError: true,
});
export type Mission = typeof missions.$inferSelect;
export type InsertMission = z.infer<typeof insertMissionSchema>;

export const insertMissionTechnicianSchema = createInsertSchema(missionTechnicians);
export type MissionTechnician = typeof missionTechnicians.$inferSelect;

export const insertMissionRobotSchema = createInsertSchema(missionRobots);
export type MissionRobot = typeof missionRobots.$inferSelect;
export const insertMissionRobotClientSchema = createInsertSchema(missionRobotsClients);
export type MissionRobotClient = typeof missionRobotsClients.$inferSelect;
export const missionRobotClientAssignmentSchema = z.object({
  robotClientId: z.string().uuid(),
  motif: z.string().nullable().optional(),
  remplacementRequis: z.boolean().default(false),
  robotRemplacementId: z.string().uuid().nullable().optional(),
  robotRemplacementSn: z.string().trim().min(1).nullable().optional(),
});
export type MissionRobotClientAssignment = z.infer<typeof missionRobotClientAssignmentSchema>;

export const insertMissionVehicleSchema = createInsertSchema(missionVehicles);
export type MissionVehicle = typeof missionVehicles.$inferSelect;

export const insertMissionCommercialSchema = createInsertSchema(missionCommercials);
export type MissionCommercial = typeof missionCommercials.$inferSelect;

export const insertRobotLocationSchema = createInsertSchema(robotLocations).omit({ id: true, createdAt: true });
export type RobotLocation = typeof robotLocations.$inferSelect;
export type InsertRobotLocation = z.infer<typeof insertRobotLocationSchema>;

export const insertTaskSchema = createInsertSchema(tasks, {
  rappelDate: zDateNullable,
  dateArchivage: zDateNullable,
  dateEcheance: z.string().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type Task = typeof tasks.$inferSelect;
export type InsertTask = z.infer<typeof insertTaskSchema>;

export const insertTaskCommentSchema = createInsertSchema(taskComments).omit({ id: true, createdAt: true });
export type TaskComment = typeof taskComments.$inferSelect;
export type InsertTaskComment = z.infer<typeof insertTaskCommentSchema>;

export const insertTaskPieceSchema = createInsertSchema(taskPieces).omit({ id: true });
export type TaskPiece = typeof taskPieces.$inferSelect;
export type InsertTaskPiece = z.infer<typeof insertTaskPieceSchema>;

export type TechnicianAssignment = {
  technicianId: string;
  isRemote?: boolean;
};

export type CreateMissionRequest = InsertMission & {
  technicianAssignments?: TechnicianAssignment[];
  technicianIds?: string[];
  robotIds?: string[];
  vehicleIds?: string[];
  commercialIds?: string[];
};

export type UpdateMissionRequest = Partial<CreateMissionRequest>;

export const insertTicketSchema = createInsertSchema(tickets, {
  receivedAt: zDateNullable,
  dateResolution: zDateNullable,
  dateArchivage: zDateNullable,
  resumeIaDate: zDateNullable,
}).omit({ id: true, ticketNumber: true, createdAt: true, updatedAt: true });
export type Ticket = typeof tickets.$inferSelect;
export type InsertTicket = z.infer<typeof insertTicketSchema>;

export const insertTicketNoteSchema = createInsertSchema(ticketNotes).omit({ id: true, createdAt: true });
export type TicketNote = typeof ticketNotes.$inferSelect;
export type InsertTicketNote = z.infer<typeof insertTicketNoteSchema>;

export const insertTicketMissionSchema = createInsertSchema(ticketMissions);
export type TicketMission = typeof ticketMissions.$inferSelect;

export const insertRobotClientSchema = createInsertSchema(robotsClients).omit({ id: true, createdAt: true, updatedAt: true });
export type RobotClient = typeof robotsClients.$inferSelect;
export type InsertRobotClient = z.infer<typeof insertRobotClientSchema>;

export const insertSavDossierSchema = createInsertSchema(savDossiers, {
  resumeIaDate: zDateNullable,
}).omit({ id: true, reference: true, createdAt: true, updatedAt: true });
export type SavDossier = typeof savDossiers.$inferSelect;
export type InsertSavDossier = z.infer<typeof insertSavDossierSchema>;

export const insertSavInterventionSchema = createInsertSchema(savInterventions).omit({ id: true, dateHeure: true, createdAt: true });
export type SavIntervention = typeof savInterventions.$inferSelect;
export type InsertSavIntervention = z.infer<typeof insertSavInterventionSchema>;

export const insertSavPieceSchema = createInsertSchema(savPieces).omit({ id: true });
export type SavPiece = typeof savPieces.$inferSelect;
export type InsertSavPiece = z.infer<typeof insertSavPieceSchema>;

export const insertRevendeurDocumentSchema = createInsertSchema(revendeurDocuments).omit({ id: true, createdAt: true });
export type RevendeurDocument = typeof revendeurDocuments.$inferSelect;
export type InsertRevendeurDocument = z.infer<typeof insertRevendeurDocumentSchema>;

export const resellerFolders = pgTable("reseller_folders", {
  id: uuid("id").primaryKey().defaultRandom(),
  resellerId: uuid("reseller_id").references(() => resellers.id).notNull(),
  nom: text("nom").notNull(),
  parentId: uuid("parent_id"),
  ordre: integer("ordre").default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const resellerFiles = pgTable("reseller_files", {
  id: uuid("id").primaryKey().defaultRandom(),
  resellerId: uuid("reseller_id").references(() => resellers.id).notNull(),
  folderId: uuid("folder_id").references(() => resellerFolders.id),
  nom: text("nom").notNull(),
  fichierUrl: text("fichier_url").notNull(),
  typeFichier: text("type_fichier"),
  taille: integer("taille"),
  ordre: integer("ordre").default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export type ResellerFolder = typeof resellerFolders.$inferSelect;
export type ResellerFile = typeof resellerFiles.$inferSelect;

export const insertRevendeurContratSchema = createInsertSchema(revendeurContrats).omit({ id: true, createdAt: true });
export type RevendeurContrat = typeof revendeurContrats.$inferSelect;
export type InsertRevendeurContrat = z.infer<typeof insertRevendeurContratSchema>;

export const insertMissionSegmentSchema = createInsertSchema(missionSegments).omit({ id: true });
export type MissionSegment = typeof missionSegments.$inferSelect;
export type InsertMissionSegment = z.infer<typeof insertMissionSegmentSchema>;

export const insertTicketMessageSchema = createInsertSchema(ticketMessages).omit({ id: true, createdAt: true });
export type TicketMessage = typeof ticketMessages.$inferSelect;
export type InsertTicketMessage = z.infer<typeof insertTicketMessageSchema>;
export const insertTicketMessageEmailSchema = createInsertSchema(ticketMessagesEmail).omit({ id: true, createdAt: true });
export type TicketMessageEmail = typeof ticketMessagesEmail.$inferSelect;
export type InsertTicketMessageEmail = z.infer<typeof insertTicketMessageEmailSchema>;

export const insertTicketPieceSchema = createInsertSchema(ticketPieces).omit({ id: true });
export type TicketPiece = typeof ticketPieces.$inferSelect;
export type InsertTicketPiece = z.infer<typeof insertTicketPieceSchema>;

// --- Vault tables ---
export const vaultConfig = pgTable("vault_config", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  pinHash: text("pin_hash").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const vaultEntries = pgTable("vault_entries", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  productName: text("product_name").notNull(),
  providerName: text("provider_name"),
  siteUrl: text("site_url"),
  category: text("category"),
  description: text("description"),
  logoUrl: text("logo_url"),
  entryType: text("entry_type").default("application"),
  login: text("login"),
  passwordEncrypted: text("password_encrypted"),
  contractName: text("contract_name"),
  hasSubscription: boolean("has_subscription").default(false),
  recurrence: text("recurrence"),
  priceMonthly: numeric("price_monthly"),
  priceAnnual: numeric("price_annual"),
  amountPaid: numeric("amount_paid"),
  subscriptionDate: date("subscription_date"),
  renewalDate: date("renewal_date"),
  commercialContact: text("commercial_contact"),
  phone: text("phone"),
  email: text("email"),
  comment: text("comment"),
  status: text("status").default("actif"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertVaultEntrySchema = createInsertSchema(vaultEntries).omit({ id: true, createdAt: true, updatedAt: true });
export type VaultEntry = typeof vaultEntries.$inferSelect;
export type InsertVaultEntry = z.infer<typeof insertVaultEntrySchema>;

export const vaultCategories = pgTable("vault_categories", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  createdAt: timestamp("created_at").defaultNow(),
});

// ─── Devis ────────────────────────────────────────────────────────────────────

export type FinancementOverride = {
  loyer?: number | null;
  maintenance?: number | null;
};

export type FinancementOverrides = Record<string, FinancementOverride>;

export const devis = pgTable("devis", {
  id: uuid("id").primaryKey().defaultRandom(),
  numero: text("numero").unique().notNull(),
  statut: text("statut", { enum: ["brouillon", "envoye", "accepte", "refuse", "expire"] }).default("brouillon"),
  clientId: uuid("client_id").references(() => clients.id),
  prospectId: uuid("prospect_id").references(() => prospects.id),
  resellerId: uuid("reseller_id").references(() => resellers.id),
  societe: text("societe"),
  contactNom: text("contact_nom"),
  contactCivilite: text("contact_civilite"),
  adresse: text("adresse"),
  objet: text("objet"),
  commercialId: uuid("commercial_id").$type<ProfileId>().references(() => profiles.id),
  lignes: jsonb("lignes").notNull().default(sql`'[]'::jsonb`),
  remiseGlobale: numeric("remise_globale", { precision: 5, scale: 2 }).default("0"),
  modalitesPaiement: text("modalites_paiement").array(),
  optionFinancement: boolean("option_financement").default(false),
  dureeFinancementDevis: text("duree_financement_devis"),
  dureesFinancement: text("durees_financement").array(),
  financementOverrides: jsonb("financement_overrides")
    .$type<FinancementOverrides>()
    .notNull()
    .default(sql`'{}'::jsonb`),
  loyerRobotAccessoiresEdite: numeric("loyer_robot_accessoires_edite", { precision: 10, scale: 2 }),
  maintenanceMensuelleEditee: numeric("maintenance_mensuelle_editee", { precision: 10, scale: 2 }),
  notes: text("notes"),
  validiteJours: integer("validite_jours").default(30),
  origine: text("origine"),
  clientFinalNom: text("client_final_nom"),
  typeDemande: text("type_demande").default("achat"),
  environnement: text("environnement"),
  dateLivraisonSouhaitee: date("date_livraison_souhaitee"),
  // Auteur (créateur réel) du devis — nullable : les devis créés avant cette
  // fonctionnalité n'ont pas d'auteur attribuable (pas de backfill possible).
  // Distinct de commercialId (champ métier modifiable = commercial responsable).
  // Résolu UNIQUEMENT côté serveur depuis req.profile.id, jamais depuis le body.
  // Immuable après création : ne doit jamais être modifié par un PUT.
  createdBy: uuid("created_by").$type<ProfileId>().references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const insertDevisSchema = createInsertSchema(devis).omit({ id: true, createdAt: true, updatedAt: true, numero: true, createdBy: true });
export type Devis = typeof devis.$inferSelect;
export type InsertDevis = z.infer<typeof insertDevisSchema>;

// ─── Commandes ────────────────────────────────────────────────────────────────

export const commandes = pgTable("commandes", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").unique(),
  zohoNumero: text("zoho_numero"),
  type: text("type").notNull().default("client"),
  clientId: uuid("client_id").references(() => clients.id),
  resellerId: uuid("reseller_id").references(() => resellers.id),
  siteLivraisonId: uuid("site_livraison_id"),
  contactNom: text("contact_nom"),
  statut: text("statut"),
  dateCommande: date("date_commande"),
  dateLivraisonPrevue: date("date_livraison_prevue"),
  montantHt: numeric("montant_ht", { precision: 10, scale: 2 }),
  montantTtc: numeric("montant_ttc", { precision: 10, scale: 2 }),
  devise: text("devise").default("EUR"),
  lignes: jsonb("lignes").default([]),
  factureUrl: text("facture_url"),
  zohoRaw: jsonb("zoho_raw"),
  derniereSync: timestamp("derniere_sync", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const zohoTokens = pgTable("zoho_tokens", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const insertCommandeSchema = createInsertSchema(commandes).omit({ id: true, createdAt: true, updatedAt: true });
export type Commande = typeof commandes.$inferSelect;
export type InsertCommande = z.infer<typeof insertCommandeSchema>;

// ─── Factures (Zoho Billing) ──────────────────────────────────────────────────

export const factures = pgTable("factures", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").unique(),
  zohoNumero: text("zoho_numero"),
  clientId: uuid("client_id").references(() => clients.id),
  resellerId: uuid("reseller_id").references(() => resellers.id),
  montantHt: numeric("montant_ht", { precision: 10, scale: 2 }),
  montantTtc: numeric("montant_ttc", { precision: 10, scale: 2 }),
  statut: text("statut"),
  dateFacture: date("date_facture"),
  dateEcheance: date("date_echeance"),
  zohoPdfUrl: text("zoho_pdf_url"),
  zohoRaw: jsonb("zoho_raw"),
  derniereSync: timestamp("derniere_sync", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const insertFactureSchema = createInsertSchema(factures).omit({ id: true, createdAt: true });
export type Facture = typeof factures.$inferSelect;
export type InsertFacture = z.infer<typeof insertFactureSchema>;

// ─── Commandes Fournisseur (Achats Pudu) ─────────────────────────────────────

export const commandesFournisseur = pgTable("commandes_fournisseur", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").unique(),
  numeroPo: text("numero_po").unique(),
  numeroPi: text("numero_pi"),
  fournisseur: text("fournisseur").default("Pudu Robotics HK Limited"),
  contactFournisseur: text("contact_fournisseur"),
  statut: text("statut").default("commandé"),
  dateCommande: date("date_commande"),
  dateLivraisonPrevue: date("date_livraison_prevue"),
  dateReception: date("date_reception"),
  montantTotal: numeric("montant_total", { precision: 10, scale: 2 }),
  devise: text("devise").default("EUR"),
  remise: numeric("remise", { precision: 10, scale: 2 }),
  fraisLogistique: numeric("frais_logistique", { precision: 10, scale: 2 }),
  lignes: jsonb("lignes").default([]),
  modalitesPaiement: text("modalites_paiement"),
  echeancesPaiement: jsonb("echeances_paiement").default([]),
  notes: text("notes"),
  zohoRaw: jsonb("zoho_raw"),
  derniereSync: timestamp("derniere_sync", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const echeancesPaiementFournisseur = pgTable("echeances_paiement_fournisseur", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  commandeId: uuid("commande_id").references(() => commandesFournisseur.id),
  label: text("label"),
  pourcentage: numeric("pourcentage", { precision: 5, scale: 2 }),
  montant: numeric("montant", { precision: 10, scale: 2 }),
  dateEcheance: date("date_echeance"),
  statut: text("statut").default("en_attente"),
  datePaiement: date("date_paiement"),
  notes: text("notes"),
});

export const insertCommandeFournisseurSchema = createInsertSchema(commandesFournisseur).omit({ id: true, createdAt: true, updatedAt: true });
export type CommandeFournisseur = typeof commandesFournisseur.$inferSelect;
export type InsertCommandeFournisseur = z.infer<typeof insertCommandeFournisseurSchema>;

export const insertEcheancePaiementFournisseurSchema = createInsertSchema(echeancesPaiementFournisseur).omit({ id: true });
export type EcheancePaiementFournisseur = typeof echeancesPaiementFournisseur.$inferSelect;
export type InsertEcheancePaiementFournisseur = z.infer<typeof insertEcheancePaiementFournisseurSchema>;

export type LigneCommande = {
  sku?: string;
  nom: string;
  quantite: number;
  prixUnitaire: number;
  prixTotal: number;
  imageUrl?: string;
};

// ─── Stock ───────────────────────────────────────────────────────────────────

export const stock = pgTable("stock", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  productId: uuid("product_id").references(() => products.id),
  sku: text("sku").notNull(),
  nom: text("nom").notNull(),
  typeProduit: text("type_produit"),
  stockPhysique: integer("stock_physique").default(0),
  stockDemo: integer("stock_demo").default(0),
  stockTampon: integer("stock_tampon").default(0),
  stockAVenir: integer("stock_a_venir").default(0),
  seuilAlerte: integer("seuil_alerte").default(0),
  actif: boolean("actif").default(true),
  notes: text("notes"),
  derniereMaj: timestamp("derniere_maj", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const stockMouvements = pgTable("stock_mouvements", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  stockId: uuid("stock_id").references(() => stock.id),
  typeMouvement: text("type_mouvement").notNull(),
  quantite: integer("quantite").notNull(),
  referenceExterne: text("reference_externe"),
  clientRevendeur: text("client_revendeur"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  createdBy: uuid("created_by").$type<ProfileId>().references(() => profiles.id),
});

export const insertStockSchema = createInsertSchema(stock).omit({ id: true, createdAt: true, derniereMaj: true });
export type Stock = typeof stock.$inferSelect;
export type InsertStock = z.infer<typeof insertStockSchema>;

export const insertStockMouvementSchema = createInsertSchema(stockMouvements).omit({ id: true, createdAt: true });
export type StockMouvement = typeof stockMouvements.$inferSelect;
export type InsertStockMouvement = z.infer<typeof insertStockMouvementSchema>;

// ─── Stock Historique ────────────────────────────────────────────────────────

export const stockHistorique = pgTable("stock_historique", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sku: text("sku").notNull(),
  dateSnapshot: date("date_snapshot").notNull(),
  stockPhysique: integer("stock_physique").default(0),
  stockDemo: integer("stock_demo").default(0),
  stockTampon: integer("stock_tampon").default(0),
  stockDisponible: integer("stock_disponible").default(0),
  ventesMois: integer("ventes_mois").default(0),
  commandesEnTransit: integer("commandes_en_transit").default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const insertStockHistoriqueSchema = createInsertSchema(stockHistorique).omit({ id: true, createdAt: true });
export type StockHistorique = typeof stockHistorique.$inferSelect;
export type InsertStockHistorique = z.infer<typeof insertStockHistoriqueSchema>;

// ─── Changelog ───────────────────────────────────────────────────────────────

export const changelog = pgTable("changelog", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  titre: text("titre").notNull(),
  contenu: text("contenu").notNull(),
  categorie: text("categorie").notNull(),
  statut: text("statut", { enum: ["brouillon", "publie"] }).notNull().default("publie"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const insertChangelogSchema = createInsertSchema(changelog).omit({ id: true, createdAt: true });
export type ChangelogEntry = typeof changelog.$inferSelect;
export type InsertChangelogEntry = z.infer<typeof insertChangelogSchema>;

export const changelogRoutineRuns = pgTable("changelog_routine_runs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  executedAt: timestamp("executed_at", { withTimezone: true }).defaultNow(),
  lastPublishedEntryDate: timestamp("last_published_entry_date", { withTimezone: true }),
  commitsAnalyzed: integer("commits_analyzed").notNull().default(0),
  draftsCreated: integer("drafts_created").notNull().default(0),
  status: text("status", { enum: ["success", "skipped", "error"] }).notNull(),
  details: text("details"),
});

export type ChangelogRoutineRun = typeof changelogRoutineRuns.$inferSelect;

// ── Portail Partenaires ───────────────────────────────────────────────────────

export const portailUsers = pgTable("portail_users", {
  id: uuid("id").primaryKey().defaultRandom(),
  resellerId: uuid("reseller_id").references(() => resellers.id, { onDelete: "cascade" }),
  supabaseAuthId: uuid("supabase_auth_id").unique(),
  email: text("email").unique().notNull(),
  role: text("role").default("contact"),
  prenom: text("prenom"),
  nom: text("nom"),
  telephone: text("telephone"),
  roleSociete: text("role_societe"),
  avatarUrl: text("avatar_url"),
  actif: boolean("actif").default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const resellerAdresses = pgTable("reseller_adresses", {
  id: uuid("id").primaryKey().defaultRandom(),
  resellerId: uuid("reseller_id").references(() => resellers.id, { onDelete: "cascade" }),
  label: text("label"),
  adresse: text("adresse").notNull(),
  codePostal: text("code_postal"),
  ville: text("ville"),
  pays: text("pays").default("France"),
  contactNom: text("contact_nom"),
  contactTelephone: text("contact_telephone"),
  typeAdresse: text("type_adresse").default("Livraison"),
  isDefault: boolean("is_default").default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const portailNotifications = pgTable("portail_notifications", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").notNull().references(() => resellers.id, { onDelete: "cascade" }),
  type: text("type").notNull().default("information"),
  titre: text("titre").notNull(),
  message: text("message"),
  lien: text("lien"),
  entiteType: text("entite_type"),
  entiteId: text("entite_id"),
  lu: boolean("lu").notNull().default(false),
  luAt: timestamp("lu_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type PortailNotification = typeof portailNotifications.$inferSelect;

export const portailTokens = pgTable("portail_tokens", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").references(() => resellers.id),
  token: text("token").unique().notNull(),
  type: text("type").default("ticket_public"),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type PortailToken = typeof portailTokens.$inferSelect;

export const insertPortailUserSchema = createInsertSchema(portailUsers).omit({ id: true, createdAt: true });
export type PortailUser = typeof portailUsers.$inferSelect;
export type InsertPortailUser = z.infer<typeof insertPortailUserSchema>;

export const insertResellerAdresseSchema = createInsertSchema(resellerAdresses).omit({ id: true, createdAt: true });
export type ResellerAdresse = typeof resellerAdresses.$inferSelect;
export type InsertResellerAdresse = z.infer<typeof insertResellerAdresseSchema>;

// --- Portail Partenaires : Espace documents ---
export const portailCategories = pgTable("portail_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  nom: text("nom").notNull(),
  parentId: uuid("parent_id").references((): any => portailCategories.id),
  ordre: integer("ordre").default(0),
  icone: text("icone"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type PortailCategorie = typeof portailCategories.$inferSelect;
export const insertPortailCategorieSchema = createInsertSchema(portailCategories).omit({ id: true, createdAt: true });
export type InsertPortailCategorie = z.infer<typeof insertPortailCategorieSchema>;

export const portailDocuments = pgTable("portail_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  titre: text("titre").notNull(),
  description: text("description"),
  categorieId: uuid("categorie_id").references(() => portailCategories.id),
  fichierUrl: text("fichier_url"),
  typeFichier: text("type_fichier"),
  visibilite: text("visibilite").default("tous"),
  revendeursIds: uuid("revendeurs_ids").array(),
  notifier: boolean("notifier").default(true),
  actif: boolean("actif").default(true),
  ordre: integer("ordre").default(0),
  tailleFichier: integer("taille_fichier"),
  storageBucket: text("storage_bucket").notNull().default("portail-documents"),
  mimeType: text("mime_type"),
  nomOrigine: text("nom_origine"),
  createdBy: uuid("created_by").references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type PortailDocument = typeof portailDocuments.$inferSelect;
export const insertPortailDocumentSchema = createInsertSchema(portailDocuments).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPortailDocument = z.infer<typeof insertPortailDocumentSchema>;

export const portailActualites = pgTable("portail_actualites", {
  id: uuid("id").primaryKey().defaultRandom(),
  titre: text("titre").notNull(),
  accroche: text("accroche"),
  contenu: text("contenu"),
  imageUrl: text("image_url"),
  documentId: uuid("document_id").references(() => portailDocuments.id),
  type: text("type").default("nouveaute"),
  prixPromo: numeric("prix_promo", { precision: 10, scale: 2 }),
  prixNormal: numeric("prix_normal", { precision: 10, scale: 2 }),
  produitId: uuid("produit_id").references(() => products.id),
  visibilite: text("visibilite").default("tous"),
  dateDebut: date("date_debut"),
  dateFin: date("date_fin"),
  actif: boolean("actif").default(true),
  isBundle: boolean("is_bundle").default(false),
  documentPdfUrl: text("document_pdf_url"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type PortailActualite = typeof portailActualites.$inferSelect;
export const insertPortailActualiteSchema = createInsertSchema(portailActualites).omit({ id: true, createdAt: true });
export type InsertPortailActualite = z.infer<typeof insertPortailActualiteSchema>;

export const portailActualiteBundle = pgTable("portail_actualite_bundle", {
  id: uuid("id").primaryKey().defaultRandom(),
  actualiteId: uuid("actualite_id").references(() => portailActualites.id, { onDelete: "cascade" }),
  productId: uuid("product_id").references(() => products.id),
  quantite: integer("quantite").default(1),
  prixUnitaire: numeric("prix_unitaire", { precision: 10, scale: 2 }),
  offert: boolean("offert").default(false),
  ordre: integer("ordre").default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type PortailActualiteBundle = typeof portailActualiteBundle.$inferSelect;
export const insertPortailActualiteBundleSchema = createInsertSchema(portailActualiteBundle).omit({ id: true, createdAt: true });
export type InsertPortailActualiteBundle = z.infer<typeof insertPortailActualiteBundleSchema>;

export const portailLectures = pgTable("portail_lectures", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").references(() => resellers.id),
  documentId: uuid("document_id").references(() => portailDocuments.id),
  actualiteId: uuid("actualite_id").references(() => portailActualites.id),
  luAt: timestamp("lu_at", { withTimezone: true }).defaultNow(),
});
export type PortailLecture = typeof portailLectures.$inferSelect;

// ─── Email Templates ─────────────────────────────────────────────────────────
export const emailTemplates = pgTable("email_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  slug: text("slug").unique().notNull(),
  categorie: text("categorie").notNull(),
  nom: text("nom").notNull(),
  description: text("description"),
  objet: text("objet").notNull(),
  contenuHtml: text("contenu_html").notNull(),
  contenuTexte: text("contenu_texte"),
  variablesDisponibles: text("variables_disponibles").array(),
  declencheur: text("declencheur").notNull(),
  actif: boolean("actif").default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type EmailTemplate = typeof emailTemplates.$inferSelect;

export const emailModeles = pgTable("email_modeles", {
  id: uuid("id").primaryKey().defaultRandom(),
  code: text("code").unique().notNull(),
  libelle: text("libelle").notNull(),
  objet: text("objet").notNull(),
  corps: text("corps").notNull(),
  cible: text("cible", { enum: ["client", "revendeur", "tous"] }).notNull().default("tous"),
  relectureObligatoire: boolean("relecture_obligatoire").notNull().default(true),
  rolesAutorises: text("roles_autorises").array().notNull(),
  actif: boolean("actif").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type EmailModele = typeof emailModeles.$inferSelect;

export const emailTemplatePreferences = pgTable("email_template_preferences", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").references(() => resellers.id, { onDelete: "cascade" }),
  templateSlug: text("template_slug").references(() => emailTemplates.slug),
  actif: boolean("actif").default(true),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type EmailTemplatePreference = typeof emailTemplatePreferences.$inferSelect;

// ─── Email Settings (header + footer config) ─────────────────────────────────
export const emailSettings = pgTable("email_settings", {
  id: uuid("id").primaryKey().defaultRandom(),
  headerMode: text("header_mode").default("couleurs"),
  headerLogoUrl: text("header_logo_url"),
  headerImageUrl: text("header_image_url"),
  headerGradientStart: text("header_gradient_start").default("#4e63f0"),
  headerGradientEnd: text("header_gradient_end").default("#2facf3"),
  headerTexte: text("header_texte").default("Jobotto"),
  footerAdresse: text("footer_adresse").default("10 Rue de la Vieille Vigne, 91300 Massy"),
  footerSiret: text("footer_siret").default("977 944 487 00023"),
  footerEmail: text("footer_email").default("contact@jobotto.fr"),
  footerSite: text("footer_site").default("www.jobotto.fr"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type EmailSettings = typeof emailSettings.$inferSelect;

// ─── Pudu Cloud — Magasins et Robots synchronisés ────────────────────────────
export const puduShops = pgTable("pudu_shops", {
  id: uuid("id").primaryKey().defaultRandom(),
  shopId: text("shop_id").unique().notNull(),
  shopName: text("shop_name"),
  companyId: text("company_id"),
  companyName: text("company_name"),
  resellerId: uuid("reseller_id").references(() => resellers.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type PuduShop = typeof puduShops.$inferSelect;

export const puduRobots = pgTable("pudu_robots", {
  id: uuid("id").primaryKey().defaultRandom(),
  robotSn: text("robot_sn").unique().notNull(),
  robotMac: text("robot_mac"),
  robotType: text("robot_type"),
  robotName: text("robot_name"),
  shopId: text("shop_id").references(() => puduShops.shopId),
  resellerId: uuid("reseller_id").references(() => resellers.id),
  lastSyncAt: timestamp("last_sync_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  productCode: text("product_code"),
  productName: text("product_name"),
  productImageUrl: text("product_image_url"),
});
export type PuduRobot = typeof puduRobots.$inferSelect;

// ─── Système de rappels ───────────────────────────────────────────────────────
export const reminderTemplates = pgTable("reminder_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  nom: text("nom").notNull(),
  description: text("description"),
  categorie: text("categorie"),
  triggerEvent: text("trigger_event").notNull(),
  delayDays: integer("delay_days").default(0),
  messageTemplate: text("message_template").notNull(),
  variablesDisponibles: text("variables_disponibles").array(),
  actif: boolean("actif").default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type ReminderTemplate = typeof reminderTemplates.$inferSelect;

export const reminders = pgTable("reminders", {
  id: uuid("id").primaryKey().defaultRandom(),
  templateId: uuid("template_id").references(() => reminderTemplates.id),
  sourceType: text("source_type").notNull(),
  sourceId: uuid("source_id"),
  titre: text("titre").notNull(),
  description: text("description"),
  assigneA: uuid("assigne_a").$type<ProfileId>().references(() => profiles.id),
  audienceType: text("audience_type"),
  audienceRoles: text("audience_roles").array(),
  rappelDate: timestamp("rappel_date", { withTimezone: true }).notNull(),
  statut: text("statut").default("actif"),
  lienUrl: text("lien_url"),
  priorite: text("priorite").default("moyenne"),
  traiteAt: timestamp("traite_at", { withTimezone: true }),
  traiteBy: uuid("traite_by").$type<ProfileId>().references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type Reminder = typeof reminders.$inferSelect;

export const maintenancesPreventives = pgTable("maintenances_preventives", {
  id: uuid("id").primaryKey().defaultRandom(),
  clientId: uuid("client_id").references(() => clients.id).notNull(),
  annee: integer("annee").notNull(),
  datePrevue: date("date_prevue").notNull(),
  dateRealisee: date("date_realisee"),
  statut: text("statut").default("a_planifier"),
  missionId: uuid("mission_id").references(() => missions.id),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type MaintenancePreventive = typeof maintenancesPreventives.$inferSelect;

// ─── Feedback unifié (Jobotech + portail partenaires + portail clients) ────────

export const feedback = pgTable("feedback", {
  id: uuid("id").primaryKey().defaultRandom(),
  numero: text("numero").unique(),
  origine: text("origine").notNull(), // 'jobotech' | 'portail_partenaire' | 'portail_client'
  type: text("type").notNull(),       // 'suggestion' | 'bug' | 'amelioration_ux' | 'idee' | 'autre'
  message: text("message").notNull(),
  auteurNom: text("auteur_nom"),
  auteurEmail: text("auteur_email"),
  partenaireId: uuid("partenaire_id").references(() => resellers.id),
  clientId: uuid("client_id"),
  pieceJointePath: text("piece_jointe_path"),
  pieceJointeUrl: text("piece_jointe_url"),
  contexteUrl: text("contexte_url"),
  userAgent: text("user_agent"),
  statut: text("statut").notNull().default("nouveau"),
  // nouveau | accepte | refuse | livre | en_cours | planifie | traite | rejete
  priorite: text("priorite").default("normale"),
  // haute | moyenne | basse | normale
  titre: text("titre"),
  moduleLabel: text("module_label"),
  decisionMotif: text("decision_motif"),
  noteInterne: text("note_interne"),
  demandeParenteId: uuid("demande_parente_id"),
  traitePar: text("traite_par"),
  traiteAt: timestamp("traite_at", { withTimezone: true }),
  emetteurNotifieAt: timestamp("emetteur_notifie_at", { withTimezone: true }),
  derniereNotifStatut: text("derniere_notif_statut"),
  // IA
  iaTitre: text("ia_titre"),
  iaResume: text("ia_resume"),
  iaTypeSuggere: text("ia_type_suggere"),
  iaModuleSuggere: text("ia_module_suggere"),
  iaSousDemandes: jsonb("ia_sous_demandes").$type<{titre: string; description: string}[]>(),
  iaTraiteAt: timestamp("ia_traite_at", { withTimezone: true }),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxTriage: index("idx_feedback_triage").on(t.origine, t.statut, t.createdAt),
  idxModule: index("idx_feedback_module").on(t.moduleLabel),
  idxParent: index("idx_feedback_parent").on(t.demandeParenteId),
}));

export const feedbackVotes = pgTable("feedback_votes", {
  id: uuid("id").defaultRandom().primaryKey(),
  feedbackId: uuid("feedback_id").notNull().references(() => feedback.id, { onDelete: "cascade" }),
  votantEmail: text("votant_email").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("feedback_votes_uniq").on(t.feedbackId, t.votantEmail),
  byFeedback: index("feedback_votes_feedback_idx").on(t.feedbackId),
}));

export const insertFeedbackSchema = createInsertSchema(feedback).omit({ id: true, createdAt: true, updatedAt: true, numero: true });
export type Feedback = typeof feedback.$inferSelect;
export type InsertFeedback = z.infer<typeof insertFeedbackSchema>;

export const feedbackReponseAttachments = pgTable("feedback_reponse_attachments", {
  id: uuid("id").primaryKey().defaultRandom(),
  feedbackId: uuid("feedback_id").notNull().references(() => feedback.id, { onDelete: "cascade" }),
  chemin: text("chemin").notNull(),
  filename: text("filename").notNull(),
  contentType: text("content_type").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});
export type FeedbackReponseAttachment = typeof feedbackReponseAttachments.$inferSelect;

export type LigneDevis = {
  id: string;
  section: "produits" | "prestations";
  produitId?: string;
  imageUrl?: string;
  sku?: string;
  nomFr: string;
  descriptionFr?: string;
  bullets?: string[];
  puHt: number;
  quantite: number;
  remise: number;
  // Champs paiement génériques — Phase 1
  produitType?: string;                                     // type du produit source (robot, machine, service, etc.)
  modePaiement?: "comptant" | "finance";                    // défaut "comptant"
  dureeFinancement?: string | null;                         // "12"|"24"|"36"|"48"|"60"|"72" si modePaiement==="finance"
  natureLigne?: "capital" | "recurrent";                    // défaut "capital" ; utilisé Phase 2 (stocké dès Phase 1)
  // Maintenance — spécifique couverture garantie
  dureeMaintenance?: string;        // sentinelle: "comptant" | "12" | "24" | "36" | "48" | "60" | "72"
  dureeCouverture?: string;         // "12"|"24"|"36"|"48"|"60"|"72" — durée de garantie (texte bullets)
  montantComptant?: number | null;  // montant global en mode comptant (null → auto puHt×dureeCouverture)
  precision?: string | null;        // suffixe optionnel sur maintenance (ex. "BellaBot — poste accueil")
};

// ─── Espace Client (client.jobotto.fr) ────────────────────────────────────────

export const clientUsers = pgTable("client_users", {
  id: uuid("id").primaryKey().defaultRandom(),
  clientId: uuid("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  email: text("email").notNull().unique(),
  passwordHash: text("password_hash").notNull(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  role: text("role").notNull().default("admin"),
  roleSociete: text("role_societe"),
  status: text("status").notNull().default("active"),
  mustChangePassword: boolean("must_change_password").notNull().default(true),
  sessionVersion: integer("session_version").notNull().default(0),
  invitedBy: uuid("invited_by"),
  welcomeEmailSentAt: timestamp("welcome_email_sent_at", { withTimezone: true }),
  lastLoginAt: timestamp("last_login_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const clientDeliveryAddresses = pgTable("client_delivery_addresses", {
  id: uuid("id").primaryKey().defaultRandom(),
  clientId: uuid("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  label: text("label").notNull(),
  adresse: text("adresse").notNull(),
  codePostal: text("code_postal").notNull(),
  ville: text("ville").notNull(),
  pays: text("pays").notNull(),
  contactNom: text("contact_nom"),
  contactTelephone: text("contact_telephone"),
  isDefault: boolean("is_default").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

export const clientProfileChanges = pgTable("client_profile_changes", {
  id: uuid("id").primaryKey().defaultRandom(),
  clientId: uuid("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  actorUserId: uuid("actor_user_id"),
  authorName: text("author_name").notNull(),
  authorEmail: text("author_email").notNull(),
  kind: text("kind").notNull(),
  changedAt: timestamp("changed_at", { withTimezone: true }).notNull().defaultNow(),
});

export const clientUserTokens = pgTable("client_user_tokens", {
  id: uuid("id").primaryKey().defaultRandom(),
  clientUserId: uuid("client_user_id").notNull().references(() => clientUsers.id, { onDelete: "cascade" }),
  type: text("type", { enum: ["activation", "reset"] }).notNull(),
  tokenHash: text("token_hash").notNull().unique(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  usedAt: timestamp("used_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
  userTypeIdx: index("idx_client_user_tokens_user_type").on(table.clientUserId, table.type),
}));

export const clientUserEmailLogs = pgTable("client_user_email_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  clientUserId: uuid("client_user_id").notNull().references(() => clientUsers.id, { onDelete: "cascade" }),
  emailType: text("email_type").notNull(),
  sentAt: timestamp("sent_at", { withTimezone: true }).defaultNow(),
  sentBy: uuid("sent_by").$type<ProfileId>().references(() => profiles.id),
  metadata: jsonb("metadata"),
});

export const clientNews = pgTable("client_news", {
  id: uuid("id").primaryKey().defaultRandom(),
  titre: text("titre").notNull(),
  accroche: text("accroche"),
  contenu: text("contenu"),
  imageUrl: text("image_url"),
  type: text("type").default("information"),
  targetRobotModels: text("target_robot_models").array(),
  actif: boolean("actif").default(true),
  dateDebut: date("date_debut"),
  dateFin: date("date_fin"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const insertClientUserSchema = createInsertSchema(clientUsers).omit({ id: true, createdAt: true, updatedAt: true });
export type ClientUser = typeof clientUsers.$inferSelect;
export type InsertClientUser = z.infer<typeof insertClientUserSchema>;

export const insertClientNewsSchema = createInsertSchema(clientNews).omit({ id: true, createdAt: true, updatedAt: true });
export type ClientNews = typeof clientNews.$inferSelect;
export type InsertClientNews = z.infer<typeof insertClientNewsSchema>;

// ─── Module Chats Revendeurs ──────────────────────────────────────────────────

export const chatCounters = pgTable("chat_counters", {
  codeCourt: varchar("code_court", { length: 5 }).primaryKey(),
  lastNumber: integer("last_number").notNull().default(0),
});

export const chats = pgTable("chats", {
  id: uuid("id").primaryKey().defaultRandom(),
  numero: varchar("numero", { length: 20 }).unique().notNull(),
  resellerId: uuid("reseller_id").notNull().references(() => resellers.id),
  codeCourt: varchar("code_court_snapshot", { length: 5 }).notNull(),
  numeroSequence: integer("numero_sequence").notNull(),
  typeDemande: text("type_demande", { enum: ["information", "installation", "sav"] }).notNull(),
  titre: varchar("titre", { length: 200 }).notNull(),
  robotSn: varchar("robot_sn", { length: 50 }),
  robotId: uuid("robot_id").references(() => puduRobots.id),
  clientFinalNom: varchar("client_final_nom", { length: 200 }),
  clientFinalAdresse: text("client_final_adresse"),
  problemeConstate: text("probleme_constate"),
  technicienJobottoId: uuid("technicien_jobotto_id").$type<ProfileId>().references(() => profiles.id),
  assigneAt: timestamp("assigne_at", { withTimezone: true }),
  statut: text("statut", { enum: ["nouveau", "ouvert", "attente_revendeur", "escalade_pudu", "ferme"] }).notNull().default("nouveau"),
  ouvertParPortailUserId: uuid("ouvert_par_portail_user_id").references(() => portailUsers.id),
  ticketId: uuid("ticket_id"),
  fermeAt: timestamp("ferme_at", { withTimezone: true }),
  fermePar: uuid("ferme_par").$type<ProfileId>().references(() => profiles.id),
  resumeIa: text("resume_ia"),
  resumeIaUpdatedAt: timestamp("resume_ia_updated_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type Chat = typeof chats.$inferSelect;
export const insertChatSchema = createInsertSchema(chats).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertChat = z.infer<typeof insertChatSchema>;

export const chatParticipants = pgTable("chat_participants", {
  id: uuid("id").primaryKey().defaultRandom(),
  chatId: uuid("chat_id").notNull().references(() => chats.id, { onDelete: "cascade" }),
  typeActeur: text("type_acteur", { enum: ["jobotto", "revendeur", "pudu"] }).notNull(),
  userId: uuid("user_id").$type<ProfileId>().references(() => profiles.id),
  portailUserId: uuid("portail_user_id").references(() => portailUsers.id),
  ajouteAt: timestamp("ajoute_at", { withTimezone: true }).defaultNow(),
  retireAt: timestamp("retire_at", { withTimezone: true }),
});
export type ChatParticipant = typeof chatParticipants.$inferSelect;

export const chatMessages = pgTable("chat_messages", {
  id: uuid("id").primaryKey().defaultRandom(),
  chatId: uuid("chat_id").notNull().references(() => chats.id, { onDelete: "cascade" }),
  auteurType: text("auteur_type", { enum: ["jobotto", "revendeur", "pudu", "systeme"] }).notNull(),
  auteurUserId: uuid("auteur_user_id").$type<ProfileId>().references(() => profiles.id),
  auteurPortailUserId: uuid("auteur_portail_user_id").references(() => portailUsers.id),
  contenu: text("contenu"),
  estNoteInterne: boolean("est_note_interne").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  luPar: jsonb("lu_par").$type<ChatReadMarker[]>().default([]),
});
export type ChatMessage = typeof chatMessages.$inferSelect;

export const chatAttachments = pgTable("chat_attachments", {
  id: uuid("id").primaryKey().defaultRandom(),
  messageId: uuid("message_id").notNull().references(() => chatMessages.id, { onDelete: "cascade" }),
  filename: varchar("filename", { length: 255 }).notNull(),
  mimeType: varchar("mime_type", { length: 100 }).notNull(),
  sizeBytes: bigint("size_bytes", { mode: "number" }).notNull(),
  storagePath: text("storage_path").notNull(),
  typeMedia: text("type_media", { enum: ["image", "video", "document"] }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type ChatAttachment = typeof chatAttachments.$inferSelect;

// ── Push Subscriptions ────────────────────────────────────────────────────────
export const pushSubscriptions = pgTable("push_subscriptions", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").references(() => profiles.id, { onDelete: "cascade" }),
  portailUserId: uuid("portail_user_id").references(() => portailUsers.id, { onDelete: "cascade" }),
  endpoint: text("endpoint").notNull(),
  p256dh: text("p256dh").notNull(),
  authKey: text("auth_key").notNull(),
  deviceLabel: text("device_label"),
  lastUsedAt: timestamp("last_used_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type PushSubscription = typeof pushSubscriptions.$inferSelect;

// ── Contacts (annuaire centralisé) ───────────────────────────────────────────
export const contacts = pgTable("contacts", {
  id: uuid("id").primaryKey().defaultRandom(),
  prenom: text("prenom").notNull(),
  nom: text("nom").notNull(),
  poste: text("poste"),
  avatarUrl: text("avatar_url"),
  email: text("email"),
  telephones: jsonb("telephones").$type<Array<{ libelle: string; numero: string; principal: boolean }>>().default([]),
  organisationType: text("organisation_type", {
    enum: ["client", "revendeur", "prospect", "fournisseur", "partenaire", "autre"],
  }),
  organisationId: uuid("organisation_id"),
  organisationLibre: text("organisation_libre"),
  notes: text("notes"),
  linkedinUrl: text("linkedin_url"),
  source: text("source", {
    enum: ["manuel", "migration_client", "migration_revendeur", "migration_prospect", "extraction_signature"],
  }).default("manuel"),
  createdBy: uuid("created_by").$type<ProfileId>().references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  lastSyncedAt: timestamp("last_synced_at", { withTimezone: true }),
}, (table) => ({
  emailIdx: index("contacts_email_idx").on(table.email),
  orgIdx: index("contacts_org_idx").on(table.organisationType, table.organisationId),
  nomIdx: index("contacts_nom_idx").on(table.nom, table.prenom),
}));
export type Contact = typeof contacts.$inferSelect;
export const insertContactSchema = createInsertSchema(contacts, {
  telephones: z.array(z.object({
    libelle: z.string(),
    numero: z.string(),
    principal: z.boolean(),
  })).optional(),
  createdAt: z.coerce.date().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  lastSyncedAt: z.coerce.date().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertContact = z.infer<typeof insertContactSchema>;

// ── Tâches-Contacts (junction) ────────────────────────────────────────────────
export const tachesContacts = pgTable("taches_contacts", {
  id: uuid("id").primaryKey().defaultRandom(),
  tacheId: uuid("tache_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id").notNull().references(() => contacts.id, { onDelete: "restrict" }),
  role: text("role"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  uniqueLink: unique("taches_contacts_unique").on(table.tacheId, table.contactId),
}));
export type TacheContact = typeof tachesContacts.$inferSelect;
export type InsertTacheContact = typeof tachesContacts.$inferInsert;

// ── Contact Duplicate Candidates (cache de détection) ────────────────────────
export const contactDuplicateCandidates = pgTable("contact_duplicate_candidates", {
  id: uuid("id").primaryKey().defaultRandom(),
  contactAId: uuid("contact_a_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  contactBId: uuid("contact_b_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  detectionReasons: jsonb("detection_reasons").$type<Array<{
    type: "nom_fuzzy" | "tel_match" | "email_similar";
    score: number;
    detail: string;
  }>>(),
  combinedScore: numeric("combined_score"),
  status: text("status", { enum: ["pending", "ignored", "merged"] }).default("pending"),
  detectedAt: timestamp("detected_at", { withTimezone: true }).defaultNow(),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  reviewedBy: uuid("reviewed_by").$type<ProfileId>().references(() => profiles.id),
}, (table) => ({
  pairUnique: unique("dup_candidates_pair_unique").on(table.contactAId, table.contactBId),
  statusIdx: index("dup_status_idx").on(table.status),
  scoreIdx: index("dup_score_idx").on(table.combinedScore),
}));
export type ContactDuplicateCandidate = typeof contactDuplicateCandidates.$inferSelect;

// ── Contact Merge Logs (audit) ────────────────────────────────────────────────
export const contactMergeLogs = pgTable("contact_merge_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  contactKeptId: uuid("contact_kept_id").notNull().references(() => contacts.id),
  contactDeletedId: uuid("contact_deleted_id").notNull(),
  contactDeletedSnapshot: jsonb("contact_deleted_snapshot").notNull().$type<{
    prenom: string;
    nom: string;
    email: string | null;
    telephones: Array<{ libelle: string; numero: string; principal: boolean }>;
    poste: string | null;
    organisationType: string | null;
    organisationId: string | null;
    organisationLibre: string | null;
    notes: string | null;
    source: string;
    createdAt: string;
  }>(),
  fieldsMerged: jsonb("fields_merged").$type<Record<string, "kept" | "taken_from_deleted">>(),
  tachesRelinkedCount: integer("taches_relinked_count").default(0),
  detectionReason: text("detection_reason"),
  similarityScore: numeric("similarity_score"),
  mergedBy: uuid("merged_by").$type<ProfileId>().references(() => profiles.id),
  mergedAt: timestamp("merged_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  keptIdx: index("merge_logs_kept_idx").on(table.contactKeptId),
  dateIdx: index("merge_logs_date_idx").on(table.mergedAt),
}));
export type ContactMergeLog = typeof contactMergeLogs.$inferSelect;

// ── Scanner produit IA ──────────────────────────────────────────────────────

export const productScans = pgTable("product_scans", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: uuid("user_id").$type<ProfileId>().notNull().references(() => profiles.id),
  photoUrl: text("photo_url").notNull(),
  filtersApplied: jsonb("filters_applied"),
  candidatesCount: integer("candidates_count"),
  aiSuggestions: jsonb("ai_suggestions"),
  selectedProductId: uuid("selected_product_id").references(() => products.id),
  userMarkedNoMatch: boolean("user_marked_no_match").default(false),
  actionTaken: text("action_taken"),
  detectionMethod: text("detection_method").notNull().default("ai_vision"),
  aiLatencyMs: integer("ai_latency_ms"),
  quotaConsumed: boolean("quota_consumed").default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ProductScan = typeof productScans.$inferSelect;

export const scanQuotaOverrides = pgTable("scan_quota_overrides", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: uuid("user_id").$type<ProfileId>().notNull().references(() => profiles.id),
  dailyLimit: integer("daily_limit").notNull(),
  validFrom: date("valid_from").notNull(),
  validUntil: date("valid_until"),
  reason: text("reason"),
  createdBy: uuid("created_by").$type<ProfileId>().notNull().references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

export type ScanQuotaOverride = typeof scanQuotaOverrides.$inferSelect;

// ── Sous-traitance partenaires ────────────────────────────────────────────────

export const PRESTATION_STATUTS = [
  "demande_recue",
  "devis_envoye",
  "devis_accepte",
  "devis_refuse",
  "intervention_planifiee",
  "intervention_realisee",
  "facture",
  "clos",
  "annule",
] as const;
export type PrestationStatut = (typeof PRESTATION_STATUTS)[number];

export const prestationsPartenaires = pgTable("prestations_partenaires", {
  id: uuid("id").primaryKey().defaultRandom(),
  numero: text("numero").notNull(),
  resellerId: uuid("reseller_id").notNull().references(() => resellers.id),
  portailUserId: uuid("portail_user_id").references(() => portailUsers.id),
  statut: text("statut", { enum: PRESTATION_STATUTS }).notNull().default("demande_recue"),
  typePrestation: text("type_prestation").notNull(),
  titre: text("titre").notNull(),
  description: text("description"),
  // Site d'intervention
  siteNom: text("site_nom"),
  siteAdresse: text("site_adresse"),
  clientFinalNom: text("client_final_nom"),
  clientFinalAdresse: text("client_final_adresse"),
  // Contact terrain sur site
  contactTerrainNom: text("contact_terrain_nom"),
  contactTerrainTel: text("contact_terrain_tel"),
  contactTerrainEmail: text("contact_terrain_email"),
  // Robot concerné
  modeleRobot: text("modele_robot"),
  numeroSerie: text("numero_serie"),
  // Description technique
  descriptionPanne: text("description_panne"),
  piecesSuspectes: text("pieces_suspectes").array().default([]),
  // Urgence
  niveauUrgence: text("niveau_urgence", { enum: ["faible", "normal", "urgent", "critique"] }).default("normal"),
  // Planning
  dateIntervention: date("date_intervention"),
  dateInterventionFin: date("date_intervention_fin"),
  // Fichiers joints (stockés en chemins relatifs dans le bucket privé)
  piecesJointes: jsonb("pieces_jointes").$type<Array<{ path: string; name: string; size: number }>>().default([]),
  // Gestion interne
  noteInterne: text("note_interne"),
  assigneA: uuid("assigne_a").$type<ProfileId>().references(() => profiles.id),
  missionId: uuid("mission_id").references(() => missions.id, { onDelete: "set null" }),
  ticketId: uuid("ticket_id").references(() => tickets.id, { onDelete: "set null" }),
  createdBy: uuid("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  // Phase 2 — Notation
  notation: integer("notation"),
  notationCommentaire: text("notation_commentaire"),
  notationAt: timestamp("notation_at", { withTimezone: true }),
  // Phase 2 — Facturation
  factureUrl: text("facture_url"),
  factureNumeroZoho: text("facture_numero_zoho"),
  factureMontant: numeric("facture_montant"),
  factureAt: timestamp("facture_at", { withTimezone: true }),
  // Phase 2 — Annulation
  motifAnnulation: text("motif_annulation"),
  // Phase 2 — Relance notation (idempotency)
  notationRelanceAt: timestamp("notation_relance_at", { withTimezone: true }),
}, (table) => ({
  resellerIdx: index("prestations_reseller_idx").on(table.resellerId),
  statutIdx: index("prestations_statut_idx").on(table.statut),
  numeroUnique: unique("prestations_numero_unique").on(table.numero),
}));
export type PrestationPartenaire = typeof prestationsPartenaires.$inferSelect;
export const insertPrestationSchema = createInsertSchema(prestationsPartenaires, {
  piecesJointes: z.array(z.object({ path: z.string(), name: z.string(), size: z.number() })).optional(),
  piecesSuspectes: z.array(z.string()).optional(),
  niveauUrgence: z.enum(["faible", "normal", "urgent", "critique"]).optional(),
  createdAt: z.coerce.date().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPrestation = z.infer<typeof insertPrestationSchema>;

export interface DevisLigne {
  description: string;
  quantite: number;
  prixUnitaire: number;
  total: number;
}

export const devisPrestations = pgTable("devis_prestations", {
  id: uuid("id").primaryKey().defaultRandom(),
  prestationId: uuid("prestation_id").notNull().references(() => prestationsPartenaires.id, { onDelete: "cascade" }),
  version: integer("version").notNull().default(1),
  lignes: jsonb("lignes").$type<DevisLigne[]>().default([]),
  montantHt: numeric("montant_ht"),
  tauxTva: numeric("taux_tva").default("20"),
  montantTtc: numeric("montant_ttc"),
  validiteJours: integer("validite_jours").default(30),
  conditions: text("conditions"),
  pdfUrl: text("pdf_url"),
  estAvenant: boolean("est_avenant").default(false),
  avenantDe: uuid("avenant_de"),
  envoye: boolean("envoye").default(false),
  envoyeAt: timestamp("envoye_at", { withTimezone: true }),
  accepte: boolean("accepte"),
  accepteAt: timestamp("accepte_at", { withTimezone: true }),
  accepteNote: text("accepte_note"),
  // Audit légal d'acceptation
  acceptationIp: text("acceptation_ip"),
  acceptationUserId: uuid("acceptation_user_id"),
  createdBy: uuid("created_by").$type<ProfileId>().references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  prestationIdx: index("devis_prestations_prestation_idx").on(table.prestationId),
}));
export type DevisPrestation = typeof devisPrestations.$inferSelect;

export const prestationsMessages = pgTable("prestations_messages", {
  id: uuid("id").primaryKey().defaultRandom(),
  prestationId: uuid("prestation_id").notNull().references(() => prestationsPartenaires.id, { onDelete: "cascade" }),
  auteur: text("auteur").notNull(),
  auteurId: uuid("auteur_id"),
  auteurType: text("auteur_type", { enum: ["internal", "portail"] }).notNull().default("internal"),
  contenu: text("contenu").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  prestationIdx: index("prestations_messages_prestation_idx").on(table.prestationId),
}));
export type PrestationMessage = typeof prestationsMessages.$inferSelect;

export const prestationsNotifications = pgTable("prestations_notifications", {
  id: uuid("id").primaryKey().defaultRandom(),
  prestationId: uuid("prestation_id").notNull().references(() => prestationsPartenaires.id, { onDelete: "cascade" }),
  type: text("type").notNull(),
  lu: boolean("lu").default(false),
  destinataireId: uuid("destinataire_id"),
  destinataireType: text("destinataire_type", { enum: ["internal", "portail"] }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  prestationIdx: index("prestations_notifs_prestation_idx").on(table.prestationId),
  luIdx: index("prestations_notifs_lu_idx").on(table.lu, table.destinataireId),
}));

// App notifications (in-app: task assignments, etc.)
export const appNotifications = pgTable("app_notifications", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").notNull().references(() => profiles.id, { onDelete: "cascade" }),
  type: text("type").notNull().default("task_assigned"),
  taskId: uuid("task_id").references(() => tasks.id, { onDelete: "cascade" }),
  ticketId: uuid("ticket_id").references(() => tickets.id, { onDelete: "cascade" }),
  bonLivraisonId: uuid("bon_livraison_id").references(() => bonsLivraison.id, { onDelete: "cascade" }),
  message: text("message"),
  read: boolean("read").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  userIdIdx: index("app_notifications_user_id_idx").on(table.userId),
  readIdx: index("app_notifications_read_idx").on(table.userId, table.read),
}));
export type AppNotification = typeof appNotifications.$inferSelect;

// ── Procédures internes ────────────────────────────────────────────────────────

export const SERVICES_PROCEDURES = [
  { slug: "technique",  label: "Technique",     icon: "Wrench"    },
  { slug: "admin",      label: "Administratif", icon: "FileText"  },
  { slug: "commercial", label: "Commercial",    icon: "Briefcase" },
] as const;
export type ServiceProcedure = typeof SERVICES_PROCEDURES[number]["slug"];

// Remplacé par un contrôle de rôle (accessRole >= admin) — voir requireGestionnaireProcedures dans routes.ts
export const GESTIONNAIRES_PROCEDURES: string[] = [];

export const proceduresCategories = pgTable("procedures_categories", {
  id: uuid("id").primaryKey().defaultRandom(),
  service: text("service").notNull(),
  parentId: uuid("parent_id"),
  nom: text("nom").notNull(),
  description: text("description"),
  ordre: integer("ordre").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type ProcedureCategorie = typeof proceduresCategories.$inferSelect;
export const insertProcedureCategorieSchema = createInsertSchema(proceduresCategories).omit({ id: true, createdAt: true, updatedAt: true });

export const proceduresDocuments = pgTable("procedures_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  service: text("service").notNull(),
  categorieId: uuid("categorie_id"),
  titre: text("titre").notNull(),
  description: text("description"),
  fichierPath: text("fichier_path").notNull(),
  fichierNom: text("fichier_nom").notNull(),
  fichierType: text("fichier_type"),
  fichierTaille: integer("fichier_taille"),
  uploadedBy: text("uploaded_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type ProcedureDocument = typeof proceduresDocuments.$inferSelect;
export const insertProcedureDocumentSchema = createInsertSchema(proceduresDocuments).omit({ id: true, createdAt: true, updatedAt: true });

// Audit logs
export const auditLogs = pgTable("audit_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id").references(() => profiles.id, { onDelete: "set null" }),
  action: text("action").notNull(),
  entityType: text("entity_type"),
  entityId: text("entity_id"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  userIdIdx: index("audit_logs_user_id_idx").on(table.userId),
  actionIdx: index("audit_logs_action_idx").on(table.action),
  createdAtIdx: index("audit_logs_created_at_idx").on(table.createdAt),
}));
export type AuditLog = typeof auditLogs.$inferSelect;

// ── Feed d'activité général (mur collectif passif) ────────────────────────────

export const activityFeed = pgTable("activity_feed", {
  id: uuid("id").primaryKey().defaultRandom(),
  actorId: uuid("actor_id").references(() => profiles.id, { onDelete: "set null" }),
  category: text("category").notNull(),
  title: text("title").notNull(),
  entityType: text("entity_type"),
  entityId: uuid("entity_id"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
  createdAtIdx: index("activity_feed_created_at_idx").on(table.createdAt),
}));
export type ActivityFeedEntry = typeof activityFeed.$inferSelect;

// ── Formulaire déclaration publique ──────────────────────────────────────────

export const SECTEURS_ACTIVITE = [
  { categorie: "Alimentation et boissons", sousCategories: [
    "Restaurants à service complet",
    "Restaurants à service limité",
    "Restaurants sans service",
  ]},
  { categorie: "Hôtellerie", sousCategories: [
    "Économique", "Milieu de gamme", "Haut de gamme", "Luxe",
  ]},
  { categorie: "Vente au détail", sousCategories: [
    "Vente au détail complète",
    "Centre commercial",
    "Commerce de détail spécialisé dans les produits alimentaires, les boissons et le tabac",
    "Commerce de détail spécialisé en produits textiles et tricots",
    "Vente au détail de produits de décoration personnelle",
    "Commerce de détail spécialisé en biens et équipements culturels et sportifs",
    "Commerce de détail spécialisé en cosmétiques et matériel médical",
    "Vente d'automobiles, de motos, de pièces détachées, de carburant et d'autres produits énergétiques",
    "Commerce de détail d'appareils électroménagers et de produits électroniques",
    "Industrie de la réparation de véhicules automobiles, de produits électroniques et de produits du quotidien",
    "Commerce de détail de quincaillerie, de meubles et de matériaux de décoration intérieure",
    "Échoppes, commerce hors magasin et autres commerces de détail",
    "Marché de gros",
    "Marché ou Foire",
  ]},
  { categorie: "Loisir et sports", sousCategories: [
    "Culture", "Casino", "Sport", "Loisirs et divertissement",
  ]},
  { categorie: "Immobilier, Facility management & Nettoyage", sousCategories: [
    "Immobilier résidentiel et de bureaux", "Facility Management", "Nettoyage",
  ]},
  { categorie: "Transport et services associés", sousCategories: [
    "Gare", "Station de bus", "Port", "Aéroport", "Métro",
  ]},
  { categorie: "Entrepôt et Logistique", sousCategories: [
    "Entrepôt général", "Entrepôt spécial", "Centre de livraison",
    "Centre de regroupement de marchandises", "Centre de répartition de marchandises",
    "Centre de transit", "Centre de stockage et de régulation",
  ]},
  { categorie: "Installation industrielle", sousCategories: [
    "Fabrication alimentaire", "Fabrication de boissons", "Fabrication de produits du tabac",
    "Industrie textile et fabrication du cuir", "Fabrication de produits textiles",
    "Fabrication de chaussures et de vêtements",
    "Fabrication de bois, produits en bois et en liège (sauf mobilier)",
    "Fabrication de produits en herbe et en matériaux tissés",
    "Impression et reproduction de supports enregistrés",
    "Fabrication de coke et de produits pétroliers raffinés",
    "Fabrication de produits chimiques",
    "Fabrication de médicaments de base et de préparations pharmaceutiques",
    "Fabrication de produits en caoutchouc et en plastique",
    "Fabrication d'autres produits minéraux non métalliques",
    "Fabrication de métaux de base",
    "Fabrication de produits métalliques (sauf machines et équipements)",
    "Fabrication de produits informatiques, électroniques et optiques",
    "Fabrication d'équipements électriques",
    "Fabrication d'autres équipements mécaniques",
    "Fabrication de voitures, remorques et semi-remorques",
    "Fabrication d'autres équipements de transport",
    "Fabrication de meubles",
    "Autre industrie manufacturière",
    "Réparation, entretien et installation de machines et d'équipements",
  ]},
  { categorie: "Santé", sousCategories: [
    "Hôpital", "Établissements de santé de première primaire",
    "Établissements de services de santé publique spécialisés",
    "Autres établissements de santé", "Établissements de soins avec hébergement",
    "Établissements de soins sans hébergement",
  ]},
  { categorie: "Éducation", sousCategories: [
    "Établissements d'éducation publics", "Éducation spécialisée",
    "Organismes de formation", "Institutions de recherche",
  ]},
] as const;

// ── Contact Liens ─────────────────────────────────────────────────────────────

export const contactLiens = pgTable("contact_liens", {
  id:            uuid("id").primaryKey().defaultRandom(),
  contactId:     uuid("contact_id").notNull().references(() => contacts.id, { onDelete: "cascade" }),
  clientId:      uuid("client_id").references(() => clients.id, { onDelete: "cascade" }),
  revendeurId:   uuid("revendeur_id").references(() => resellers.id, { onDelete: "cascade" }),
  prospectId:    uuid("prospect_id").references(() => prospects.id, { onDelete: "cascade" }),
  typeLien:      text("type_lien").notNull().default("prestataire"),
  precisionLien: text("precision_lien"),
  missionId:     uuid("mission_id").references(() => missions.id, { onDelete: "set null" }),
  notes:         text("notes"),
  categorie:     text("categorie").notNull().default("intervenant_exterieur"),
  createdAt:     timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  createdBy:     uuid("created_by"),
});

export const insertContactLienSchema = createInsertSchema(contactLiens, {
  createdAt: z.coerce.date().optional(),
  categorie: z.enum(["membre_equipe", "intervenant_exterieur"]).default("intervenant_exterieur"),
}).omit({ id: true, createdAt: true });
export type ContactLien = typeof contactLiens.$inferSelect;
export type InsertContactLien = z.infer<typeof insertContactLienSchema>;

// ── Mission Type Settings ─────────────────────────────────────────────────────

export const missionTypeSettings = pgTable("mission_type_settings", {
  typeKey:   text("type_key").primaryKey(),
  libelle:   text("libelle").notNull(),
  couleur:   text("couleur").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

export const insertMissionTypeSettingsSchema = createInsertSchema(missionTypeSettings);
export type MissionTypeSettings = typeof missionTypeSettings.$inferSelect;
export type InsertMissionTypeSettings = typeof missionTypeSettings.$inferInsert;

// ── Onboarding revendeur ──────────────────────────────────────────────────────

export const revendeurContacts = pgTable("revendeur_contacts", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").notNull().references(() => resellers.id, { onDelete: "cascade" }),
  nom: text("nom").notNull(),
  prenom: text("prenom").notNull(),
  poste: text("poste"),
  email: text("email"),
  ligneDirecte: text("ligne_directe"),
  roles: text("roles").array().notNull().default(sql`'{}'`),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqEmail: uniqueIndex("revendeur_contacts_email_uniq").on(t.revendeurId, sql`lower(${t.email})`).where(sql`${t.email} IS NOT NULL`),
}));
export type RevendeurContact = typeof revendeurContacts.$inferSelect;
export type InsertRevendeurContact = typeof revendeurContacts.$inferInsert;

export const gammePudu = pgTable("gamme_pudu", {
  id: uuid("id").primaryKey().defaultRandom(),
  modele: text("modele").notNull().unique(),
  actif: boolean("actif").notNull().default(true),
  ordre: integer("ordre"),
  dateAjout: timestamp("date_ajout", { withTimezone: true }).defaultNow(),
});
export type GammePudu = typeof gammePudu.$inferSelect;
export type InsertGammePudu = typeof gammePudu.$inferInsert;

export const certificationsRevendeur = pgTable("certifications_revendeur", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").notNull().unique().references(() => resellers.id, { onDelete: "cascade" }),
  numeroAutorisation: text("numero_autorisation").notNull().unique(),
  dateRemise: date("date_remise").notNull(),
  dateValidite: date("date_validite").notNull(),
  statut: text("statut").notNull().default("valide"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export type CertificationRevendeur = typeof certificationsRevendeur.$inferSelect;
export type InsertCertificationRevendeur = typeof certificationsRevendeur.$inferInsert;

export const onboardingRelances = pgTable("onboarding_relances", {
  id: uuid("id").primaryKey().defaultRandom(),
  revendeurId: uuid("revendeur_id").notNull().references(() => resellers.id, { onDelete: "cascade" }),
  etape: text("etape").notNull(),
  numeroRelance: integer("numero_relance").notNull(),
  envoyeeAt: timestamp("envoyee_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("onboarding_relances_uniq").on(t.revendeurId, t.etape, t.numeroRelance),
}));
export type OnboardingRelance = typeof onboardingRelances.$inferSelect;
export type InsertOnboardingRelance = typeof onboardingRelances.$inferInsert;

// ─────────────────────────────────────────────────────────────────────────────
// MODULE RH / CVthèque — Phase 0 + 1B
// ─────────────────────────────────────────────────────────────────────────────

export const lieuxRecrutement = pgTable("lieux_recrutement", {
  id: uuid("id").primaryKey().defaultRandom(),
  libelle: text("libelle").notNull(),
  ville: text("ville").notNull(),
  codePostal: text("code_postal"),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  actif: boolean("actif").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type LieuRecrutement = typeof lieuxRecrutement.$inferSelect;
export type InsertLieuRecrutement = typeof lieuxRecrutement.$inferInsert;

export const campagnes = pgTable("campagnes", {
  id: uuid("id").primaryKey().defaultRandom(),
  intitule: text("intitule").notNull(),
  filiere: text("filiere"),
  poste: text("poste"),
  contrat: text("contrat"),
  statut: text("statut").notNull().default("ouverte"),
  lieuId: uuid("lieu_id"),
  dateDebut: date("date_debut"),
  dateFin: date("date_fin"),
  candidatRetenuId: uuid("candidat_retenu_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type Campagne = typeof campagnes.$inferSelect;
export type InsertCampagne = typeof campagnes.$inferInsert;

export const candidats = pgTable("candidats", {
  id: uuid("id").primaryKey().defaultRandom(),
  campagneId: uuid("campagne_id"),
  filiere: text("filiere"),
  nom: text("nom"),
  prenom: text("prenom"),
  sexe: text("sexe"),
  civilite: text("civilite"),
  email: text("email"),
  telephone: text("telephone"),
  emailNormalise: text("email_normalise"),
  telephoneNormalise: text("telephone_normalise"),
  ville: text("ville"),
  codePostal: text("code_postal"),
  posteVise: text("poste_vise"),
  source: text("source"),
  contratRecherche: text("contrat_recherche"),
  rythme: text("rythme"),
  formation: text("formation"),
  permisB: boolean("permis_b"),
  statutPipeline: text("statut_pipeline").notNull().default("nouveau"),
  decision: text("decision"),
  niveau: smallint("niveau"),
  evaluationCv: smallint("evaluation_cv"),
  horsZone: boolean("hors_zone").notNull().default(false),
  latitude: doublePrecision("latitude"),
  longitude: doublePrecision("longitude"),
  horsZoneSuggere: boolean("hors_zone_suggere"),
  distanceKm: doublePrecision("distance_km"),
  avis: text("avis"),
  appreciations: text("appreciations"),
  resumeEntretien: text("resume_entretien"),
  pretentionBrut: text("pretention_brut"),
  consentementCvtheque: boolean("consentement_cvtheque").notNull().default(false),
  dateAnnonce: date("date_annonce"),
  datePostulation: date("date_postulation"),
  dateEntretien: date("date_entretien"),
  archivedAt: timestamp("archived_at", { withTimezone: true }),
  recruteur: text("recruteur"),
  importBrut: jsonb("import_brut"),
  iaStatut: text("ia_statut"),
  iaTexteBrut: text("ia_texte_brut"),
  iaProposition: jsonb("ia_proposition"),
  iaTraiteAt: timestamp("ia_traite_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type Candidat = typeof candidats.$inferSelect;
export type InsertCandidat = typeof candidats.$inferInsert;

export const candidatPieces = pgTable("candidat_pieces", {
  id: uuid("id").primaryKey().defaultRandom(),
  candidatId: uuid("candidat_id").notNull(),
  type: text("type").notNull(),
  nomFichier: text("nom_fichier"),
  storagePath: text("storage_path").notNull(),
  tailleOctets: bigint("taille_octets", { mode: "number" }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});
export type CandidatPiece = typeof candidatPieces.$inferSelect;
export type InsertCandidatPiece = typeof candidatPieces.$inferInsert;

export const modelesMail = pgTable("modeles_mail", {
  id: uuid("id").primaryKey().defaultRandom(),
  cle: text("cle").notNull().unique(),
  libelle: text("libelle").notNull(),
  objet: text("objet").notNull(),
  corps: text("corps").notNull(),
  declencheur: text("declencheur"),
  actif: boolean("actif").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type ModeleMail = typeof modelesMail.$inferSelect;
export type InsertModeleMail = typeof modelesMail.$inferInsert;

export const driveDossiersMappés = pgTable("drive_dossiers_mappes", {
  id: uuid("id").primaryKey().defaultRandom(),
  driveFolderId: text("drive_folder_id").notNull().unique(),
  driveFolderName: text("drive_folder_name").notNull(),
  campagneId: uuid("campagne_id"),
  actif: boolean("actif").notNull().default(true),
  lastSyncAt: timestamp("last_sync_at", { withTimezone: true }),
  lastSyncRecap: jsonb("last_sync_recap"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type DriveDossierMappé = typeof driveDossiersMappés.$inferSelect;
export type InsertDriveDossierMappé = typeof driveDossiersMappés.$inferInsert;

export const driveFichiersIngeres = pgTable("drive_fichiers_ingeres", {
  id: uuid("id").primaryKey().defaultRandom(),
  driveFileId: text("drive_file_id").notNull().unique(),
  candidatId: uuid("candidat_id"),
  driveFolderId: text("drive_folder_id"),
  ingereAt: timestamp("ingere_at", { withTimezone: true }).notNull().defaultNow(),
});
export type DriveFichierIngere = typeof driveFichiersIngeres.$inferSelect;

export const candidatMails = pgTable("candidat_mails", {
  id: uuid("id").primaryKey().defaultRandom(),
  candidatId: uuid("candidat_id").notNull(),
  modeleCle: text("modele_cle"),
  objetEnvoye: text("objet_envoye"),
  corpsEnvoye: text("corps_envoye"),
  statut: text("statut").notNull().default("brouillon"),
  envoyePar: text("envoye_par"),
  sentAt: timestamp("sent_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});
export type CandidatMail = typeof candidatMails.$inferSelect;
export type InsertCandidatMail = typeof candidatMails.$inferInsert;

export const productSkus = pgTable("product_skus", {
  id: uuid("id").primaryKey().defaultRandom(),
  productId: uuid("product_id").notNull().references(() => products.id, { onDelete: "cascade" }),
  sku: text("sku").notNull(),
  couleur: text("couleur"),
  isCurrent: boolean("is_current").notNull().default(false),
  ordre: integer("ordre"),
  dateAjout: timestamp("date_ajout", { withTimezone: true }).notNull().defaultNow(),
  source: text("source"),
  modifiePar: text("modifie_par"),
});
export type ProductSku = typeof productSkus.$inferSelect;
export type InsertProductSku = typeof productSkus.$inferInsert;

// ── Moteur d'états — historique de transitions append-only ────────────────────

export const entityEvents = pgTable("entity_events", {
  id: uuid("id").primaryKey().defaultRandom(),
  entityType: text("entity_type").notNull(),
  entityId: uuid("entity_id").notNull(),
  eventType: text("event_type").notNull().default("status_change"),
  fromState: text("from_state"),
  toState: text("to_state").notNull(),
  actorId: uuid("actor_id").references(() => profiles.id, { onDelete: "set null" }),
  source: text("source", { enum: ["human", "system", "webhook"] }).notNull().default("human"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
  entityIdx: index("entity_events_entity_idx").on(table.entityType, table.entityId, table.createdAt),
  eventTypeIdx: index("entity_events_event_type_idx").on(table.eventType, table.createdAt),
  createdAtIdx: index("entity_events_created_at_idx").on(table.createdAt),
}));
export type EntityEvent = typeof entityEvents.$inferSelect;

// ── Bons de livraison (BL natifs, 1 devis accepté = 1 BL) ──────────────────────
// Statut piloté par texte + Zod (pas d'enum natif PG), cohérent avec la convention
// du projet pour rester 100% additif sur les futures évolutions de statut.
export const BON_LIVRAISON_STATUTS = [
  "a_preparer",
  "en_preparation",
  "prete",
  "livree_partielle",
  "livree",
  "a_facturer",
  "cloturee",
] as const;

export const bonsLivraison = pgTable("bons_livraison", {
  id: uuid("id").primaryKey().defaultRandom(),
  devisId: uuid("devis_id").notNull().references(() => devis.id).unique(),
  numeroBl: text("numero_bl").unique().notNull(),
  statut: text("statut", { enum: BON_LIVRAISON_STATUTS }).notNull().default("a_preparer"),
  lignes: jsonb("lignes").notNull().default(sql`'[]'::jsonb`),
  transporteur: text("transporteur"),
  datePassageTransporteur: timestamp("date_passage_transporteur", { withTimezone: true }),
  numeroSuivi: text("numero_suivi"),
  nbColis: integer("nb_colis"),
  nbPalettes: integer("nb_palettes"),
  etiquetteDemandeAt: timestamp("etiquette_demandee_at", { withTimezone: true }),
  factuationDemandeAt: timestamp("facturation_demandee_at", { withTimezone: true }),
  createdBy: uuid("created_by").$type<ProfileId>().references(() => profiles.id),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

export const insertBonLivraisonSchema = createInsertSchema(bonsLivraison, {
  statut: z.enum(BON_LIVRAISON_STATUTS),
}).omit({ id: true, createdAt: true, updatedAt: true, numeroBl: true, createdBy: true });
export type BonLivraison = typeof bonsLivraison.$inferSelect;
export type InsertBonLivraison = z.infer<typeof insertBonLivraisonSchema>;

export interface LigneBonLivraison {
  sku: string;
  designation: string;
  quantite: number;         // quantité du devis (conservé pour compat)
  produitType: string;
  imageUrl?: string | null;   // vignette produit (copiée depuis la ligne devis à la création)
  quantiteCommandee?: number; // = quantite (explicite depuis 1bis)
  quantiteLivree?: number;    // accumulé sur toutes les expéditions
  resteALivrer?: number;      // = quantiteCommandee - quantiteLivree
  sn?: string[];              // SN de TOUTES les unités livrées (cumulé)
  dateExpedition?: string;    // date de la dernière (ou unique) expédition
}
